import {
  CellClassParams,
  CellDoubleClickedEvent,
  CellKeyDownEvent,
  CellValueChangedEvent,
  ColDef,
  EditableCallbackParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellEditorParams,
  RowDataChangedEvent,
  RowNode,
} from '@ag-grid-community/core'
import { HttpParams } from '@angular/common/http'
import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core'
import { FormControl, ValidationErrors } from '@angular/forms'
import {
  heliosDefaultColDef,
  heliosDefaultColWidth,
  heliosSizingAdjustments,
} from '@helios/ag-grid-config'
import { TRADE_ENTRIES } from '@helios/common-api'
import {
  RootState,
  selectActiveSession,
  selectActiveSessionIdentifier,
  selectActiveSessionReconDate,
  selectActiveUserId,
  selectActiveWindowDate,
  selectIsHplAccount,
  selectIsInternalUser,
  selectUserPolicy,
  setCommandsMetrics,
  UserPolicy,
} from '@helios/state-management'
import { TelemetryService } from '@helios/telemetry'
import {
  NgbActiveModal,
  NgbModal,
  NgbModalOptions,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { AgGridApi, NfTooltipParams, resizeColumns } from '@northfork/ag-grid'
import {
  getInlineEditColumnType,
  InlineEditDataType,
  InlineEditorParams,
} from '@northfork/ag-grid-angular'
import { InlineEditDisplayOptions, ToastService } from '@northfork/ng-basics'
import { isAfter } from 'date-fns'
import { Observable, Subject } from 'rxjs'
import {
  debounceTime,
  filter,
  map,
  retry,
  take,
  takeUntil,
  takeWhile,
} from 'rxjs/operators'
import { CounterParty } from '../../../model/counter-party.model'
import {
  DataMapTypes,
  reserveBalanceFieldKeyFromLabel,
  reserveBalanceFieldOptions,
} from '../../../model/draw-model'
import { InventoryType } from '../../../model/inventory-type.model'
import { AccountDetails } from '../../../model/single-transaction/account-details.model'
import { CellMatched } from '../../../model/single-transaction/cell-matched.model'
import { CreateEditTransactionRequest } from '../../../model/single-transaction/create-edit-transaction.model'
import {
  ExternalDataSourceTypeId,
  ExternalUnmatchedTransaction,
} from '../../../model/single-transaction/external-unmatched-transaction'
import { LotInventoryConfig } from '../../../model/single-transaction/lot-inventory-config.model'
import {
  HistoricalBalanceData,
  TransactionModalInput,
  TransactionModalMode,
} from '../../../model/single-transaction/popup-input.model'
import { SleeveSimpleAccounts } from '../../../model/single-transaction/sleeve-simple-accounts.model'
import {
  AmortDataTrade,
  AmortDataTradeRequest,
} from '../../../model/single-transaction/trade/amort-data-trade.model'
import {
  TradeEntryModel,
  tradeField,
} from '../../../model/single-transaction/trade/trade-entry.model'
import {
  Trade,
  TradeDataMap,
} from '../../../model/single-transaction/trade/trade-transaction.model'
import { TransactionTypeLabel } from '../../../model/single-transaction/transaction'
import { TransactionMatch } from '../../../model/single-transaction/transaction-match'
import {
  TradeEntryValidationView,
  ValidationExternalTransactionData,
} from '../../../model/single-transaction/validation-view.model'
import {
  TranCategory,
  TranCategoryUrl,
} from '../../../model/transaction-category.model'
import {
  ENTRIES,
  EXITS,
  TransactionTypeEnum,
  TransactionTypeKey,
} from '../../../model/transaction-type.model'
import {
  ErrorSeverity,
  ValidationInfo,
} from '../../../model/validation-response.model'
import { CopyService } from '../../../services/copy.service'
import { CreateEditTransactionService } from '../../../services/create-edit-transaction.service'
import { ModalFocusService } from '../../../services/modal-focus.service'
import { PasteService } from '../../../services/single-transaction/paste.service'
import { Calculators } from '../../../util/calculators.util'
import {
  ACCOUNT_REQUIRED_ERROR,
  ACCOUNT_REQUIRED_FIELD_WARNING,
  activateDialog,
  addWarningDetails,
  calculateDiscount,
  clearAllInvalidations,
  createMetadata,
  emptySelectedCellsOnValidKeyPress,
  fillHandleDoubleClickHandlerSingleDialog,
  focusFieldRowEditor,
  formatNumberWithThousandSeparator,
  getBuyTypes,
  getCounterPartyFromName,
  getDefaultUnknownCounterParty,
  getDrawTypes,
  getFieldValidatorFormatter,
  getStringISOForDate,
  getTradeTypes,
  getValidInventory,
  handleValidation,
  internalAssetIdFilterGridOptions,
  isDialogComponentInActive,
  prepareMultiViewForMultiMatchValidations,
  replayDeferredLookups,
  SINGLE_DIALOG_EVENT_NAME,
  SINGLE_DIALOG_VALIDATION_EVENT_NAME,
  singleDialogMetricsDetail,
  TransactionType_FOR_SHORT,
} from '../../../util/common-utils'
import { TransactionConstants } from '../../../util/constants'
import { getLoanFieldValues } from '../../../util/loan-field.util'
import {
  createDynamicRowsForCWTransaction,
  createDynamicRowsforExternalTransaction,
} from '../../../util/dynamic-fields'
import {
  isEditModal,
  isExternalTransaction,
} from '../../../util/popup-input-util'
import {
  createTradeEntryFromExternalTransactionCustody,
  createTradeEntryFromExternalTransactionTrading,
  createTradeEntryFromHistoricalBalance,
  editTradeEntryFromTransaction,
  generateTradeEntry,
  getCollateralBroker,
  tradeEntryRecordToArray,
} from '../../../util/trade-data-mapper-util'
import { getRequestModeFromModalMode } from '../../../util/validation-response-util'
import { Security } from './../../../model/security.model'
import { CalculatePriceComponent } from './calculate-price/calculate-price.component'

@Component({
  selector: 'hs-trade-entry',
  templateUrl: './trade-entry.component.html',
  styleUrls: ['./trade-entry.component.scss'],
  providers: [PasteService, CopyService],
})
export class TradeEntryModalComponent implements OnInit, OnDestroy {
  @Input() transInput?: TransactionModalInput

  title = 'Create Trade for account: '
  formatOption = '1.1-16'
  getFieldValidatorFormatter = getFieldValidatorFormatter
  formSubmitting = false
  transactionLoaded = false
  security?: Security
  rowNodeMap = new Map<string, RowNode>()
  conditionalAdvancedRows: TradeEntryModel[] = [] //internal
  applyHPL = false
  // True while an aggregate account is selected but no concrete sub-account has been
  // picked yet. Fields stay editable; validation is deferred and edited fields are flagged.
  aggregateAccountUnresolved = false
  rowData: TradeEntryModel[] = []
  colDefs: ColDef[] = this.getColDefs()
  inactiveRows = new Map()
  gridOptions: GridOptions = this.createGridOptions()
  originalTransaction?: Trade
  userId?: number
  forbiddenErrors = new FormControl('')
  hasDeveloperAccess = false

  transTypes = getTradeTypes().map(t => t.name)
  drawTypes = getDrawTypes().map(t => t.name)
  reserveBalanceFieldOptions = reserveBalanceFieldOptions
  buyTypes = getBuyTypes().map(t => t.name)
  dataMap?: TradeDataMap = {} as TradeDataMap
  note = new FormControl('')
  displayAdvancedRows = new FormControl(false)
  clearTransaction = new FormControl(true)
  reprocessTransfers = new FormControl(false)
  advancedRows: TradeEntryModel[] = [] //internal
  sessionId?: string
  accountId?: number
  sleeveSimpleAccount: SleeveSimpleAccounts[] = []
  reconDate?: Date
  windowDate?: Date
  collateralBroker?: string
  isCollateralBrokerAccount = false
  isDataValid = false
  isCellEditing = false
  isValidating = false
  counterPartyList: CounterParty[] = []
  accountDetails?: AccountDetails
  accountDetailsAggregate?: AccountDetails
  tradingAccountName?: string
  errorFields: string[] = []
  gridApi?: AgGridApi
  executionId?: number
  externalTranTypeLabel?: TransactionTypeLabel
  externalAccountId?: number
  isExternalAccountSleeveSimple = false
  isInternalUser = false
  originalCusip?: string
  noteWarning: ValidationErrors | null = null
  hasTransactionMatchCustody = false
  hasTransactionMatchTrading = false
  isPostDateFuture = false
  isSettleDateFuture = false
  transactionMatchesCustody?: TransactionMatch[]
  transactionMatchesTrading?: TransactionMatch[]
  accountName?: string
  accountNameAgg?: string
  calculatedUnitsByMatch = new Map<string, number>()
  consolidatedFields = new Set([
    'proceeds',
    'dollarAccrued',
    'commission',
    'units',
    'originationFee',
    'exitFee',
    'exitFeeReceivable',
    'drawFee',
    'prepaymentPenaltyFee',
    'prepaidInterest',
    'interestDue',
    'drawType',
    'reserveBalanceField',
    'buyType',
  ])
  compId = 'trade-' + Math.random()
  activeInstances: NgbModalRef[] = []
  loadingText = TransactionConstants.LOAD_TRANSACTION
  errorText = ''
  errorDetails: unknown
  errorHeader: string | undefined
  yield = 0
  startTime = Date.now()
  private readonly destroySubject$ = new Subject()
  displayDynamicRows = new FormControl(false)
  dynamicRows: TradeEntryModel[] = []
  propertiesTitle = 'No Table Properties set'

  constructor(
    public toastService: ToastService,
    readonly activeModal: NgbActiveModal,
    readonly store: Store<RootState>,
    readonly createEditTransactionService: CreateEditTransactionService,
    private readonly pasteService: PasteService,
    private readonly copyService: CopyService,
    private readonly modalService: NgbModal,
    private readonly elRef: ElementRef,
    private readonly telemetryService: TelemetryService,
    private readonly modalFocusService: ModalFocusService,
  ) {
    this.modalService.activeInstances
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(activeInstances => {
        this.activeInstances = activeInstances
      })
    this.store
      .select(selectActiveSessionIdentifier)
      .pipe(take(1))
      .subscribe((sessionId?: string) => {
        this.sessionId = sessionId
      })

    this.store
      .select(selectIsHplAccount)
      .pipe(take(1))
      .subscribe(applyHPL => {
        this.applyHPL = applyHPL
      })

    this.store
      .select(selectIsInternalUser)
      .pipe(take(1))
      .subscribe(isInternalUser => {
        this.isInternalUser = isInternalUser ?? false
        this.gridApi?.api.onFilterChanged()
      })

    this.store
      .select(selectActiveSession)
      .pipe(take(1))
      .subscribe(session => {
        this.accountId = session?.loadedAccountIds[0]
      })
    this.store
      .select(selectActiveSessionReconDate)
      .pipe(take(1))
      .subscribe((reconDate?: Date) => {
        this.reconDate = reconDate
      })
    this.store
      .select(selectActiveUserId)
      .pipe(take(1))
      .subscribe((userId?: number) => {
        this.userId = userId
      })
    this.store
      .select(selectActiveWindowDate)
      .pipe(take(1))
      .subscribe(windowDate => {
        this.windowDate = windowDate
      })
    this.store
      .select(selectUserPolicy)
      .pipe(
        takeWhile(userPolicy => userPolicy == null, true),
        filter((userPolicy): userPolicy is UserPolicy => userPolicy != null),
        map(userPolicy => userPolicy.accessDeveloperTools ?? false),
      )
      .subscribe(userPolicy => {
        this.hasDeveloperAccess = userPolicy
      })
  }

  ngOnDestroy(): void {
    this.copyService.destroy()
    this.pasteService.destroy()

    this.destroySubject$.next()
    this.destroySubject$.complete()
  }

  async ngOnInit() {
    activateDialog(this.activeInstances, this.compId, this.elRef)

    this.modalFocusService
      .outsideFocus(this.elRef)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(() => this.disableGridFocus())

    const rowData = tradeEntryRecordToArray(
      generateTradeEntry(
        this.reconDate
          ? this.reconDate.toString()
          : getStringISOForDate(new Date()),
      ),
    )
    await this.setRowData(rowData)
    this.subscribeFormControls()
  }

  getProperties(): string[] {
    return [
      this.displayAdvancedRows.value
        ? 'Showing Advanced Rows'
        : 'Not Showing Advanced Rows',
    ]
  }
  subscribeFormControls() {
    this.note.valueChanges.pipe(debounceTime(500)).subscribe(() => {
      if (this.gridApi) this.validate(this.gridApi?.api)
    })

    this.clearTransaction.valueChanges.pipe(debounceTime(500)).subscribe(() => {
      if (this.gridApi) this.validate(this.gridApi?.api)
    })
  }

  async init() {
    this.loadCounterpartiesList()
    if (this.transInput && isEditModal(this.transInput)) {
      this.title = this.transInput.readOnly
        ? 'View Trade for account: '
        : 'Edit Trade for account: '
      await this.plugEditTradeData(
        this.sessionId,
        this.transInput?.transactionId,
        this.accountId,
        this.transInput.securityId,
      )
    } else {
      if (isExternalTransaction(this.transInput) && this.accountId) {
        this.title = 'Post Trade for account:  '
        await this.plugExternalUnmatchedDataView(
          Number(this.transInput?.transactionId),
          this.accountId,
        )
        this.adjustInventoryType()
        this.adjustUnitsAndRelatedFieldsForTransType(
          this.rowNodeMap.get('transTypeCode')?.data
            .value as TransactionTypeKey,
        )
      } else if (
        this.transInput?.positionData != undefined &&
        this.transInput.modalMode === TransactionModalMode.CreateFromPosition &&
        'unitDifference' in this.transInput.positionData
      ) {
        this.createRowDataForHistoricalBalance(this.transInput.positionData)
      } else {
        this.loadAccountDetails(this.accountId)
        this.loadInventoryConfigData(this.accountId)
        this.setupInitialData(this.rowData)
        this.transactionLoaded = true
      }
    }
    this.loadInventoryConfigData(this.accountId)
    this.unclearTransactionInitially()
    this.checkRowEligibilities()
  }

  loadCounterpartiesList() {
    // CounterpartyList loading
    this.createEditTransactionService
      .loadCounterParties()
      .subscribe(counterparties => {
        this.counterPartyList = counterparties
      })
  }

  loadAccountDetails(accountId?: number) {
    if (accountId) {
      this.errorDetails = ''
      this.createEditTransactionService.loadAccountDetails(accountId).subscribe(
        accountDetails => {
          this.accountDetails = accountDetails
          this.setAccountDisplayName()
          if (this.gridApi) {
            this.validate(this.gridApi.api)
          }
        },
        err => {
          this.errorHeader = 'Error Loading Account Details'
          this.errorDetails = err.error
        },
      )
    }
  }

  loadInventoryConfigData(accountId?: number) {
    if (accountId) {
      this.createEditTransactionService
        .loadInventoryConfig(accountId)
        .subscribe(inventoryConfig => {
          this.isCollateralBrokerAccount =
            inventoryConfig.inventoryGroupMethod == 'COLLATERAL_BROKER'
          if (this.isCollateralBrokerAccount) {
            this.determineAndPlugCollateral()
          }
        })
    }
  }

  loadAmortDetails(defaultBasis: number) {
    const transaction = this.prepareTransactionData('value', false)

    // Calculate commission per unit if transaction has commission and units
    if (transaction.units) {
      // Get price scalar from security
      const priceScalar = this.security?.securityMasterData?.priceScalar ?? 1

      // Store original commission for later use if needed
      const originalCommission = transaction.commission

      // Calculate commission per unit: Total Commission/Price Scalar/Units
      transaction.commission =
        originalCommission / priceScalar / Math.abs(transaction.units)
    }

    const request: AmortDataTradeRequest = {
      defaultBasis: defaultBasis,
      transaction: transaction,
    }
    if (!this.shouldLoadAmortDetails(request.transaction as Trade)) return
    this.createEditTransactionService.loadAmortDataForTrade(request).subscribe(
      amortData => {
        this.errorDetails = ''
        this.setAmortDetails(amortData)
      },
      err => {
        this.errorHeader = 'Error loading Amort Details'
        this.errorDetails = err.error
      },
    )
  }

  shouldLoadAmortDetails(transaction: Trade): boolean {
    if (
      transaction.entryDate &&
      transaction.postDate &&
      transaction.settleDate &&
      transaction.securityId &&
      transaction.accountId != 0 &&
      transaction.price &&
      TRADE_ENTRIES.includes(TransactionTypeEnum[transaction.tranType])
    )
      return true
    return false
  }

  setAmortDetails(amortData: AmortDataTrade) {
    const amortTargetDate = this.rowNodeMap.get('amortTargetDate')
    const amortTargetPrice = this.rowNodeMap.get('amortTargetPrice')
    this.yield = amortData.yield ?? 0
    this.rowNodeMap
      .get('amortTargetDate')
      ?.setDataValue('thirdParty', amortData.amortTargetDate)
    this.rowNodeMap
      .get('amortTargetPrice')
      ?.setDataValue('thirdParty', amortData.amortTargetPrice)
    if (amortTargetDate?.data.value == '')
      amortTargetDate?.setDataValue('value', amortData.amortTargetDate)
    if (amortTargetPrice?.data.value == '')
      amortTargetPrice?.setDataValue('value', amortData.amortTargetPrice)
  }

  async updateSecurityBasedFields(
    externalTransactionData: ExternalUnmatchedTransaction,
    defaultBasis: number,
    source: 'custody' | 'trading',
    security?: Security,
  ) {
    // Calculate if we have the data
    // Otherwise push over external value if it exists
    // Otherwise blank
    let clearwaterUnits: number | undefined = undefined
    if (security) {
      clearwaterUnits = Calculators.calculateUnits(
        externalTransactionData,
        security,
        Calculators.calculateCombinedFactor(this.security),
      )

      if (!externalTransactionData.accruedAmount) {
        this.calculateDollarAccrued(
          'value',
          clearwaterUnits,
          externalTransactionData.factor ??
            Calculators.calculateCombinedFactor(this.security),
        )
      }
    } else if (externalTransactionData.units) {
      clearwaterUnits = externalTransactionData.units
    }

    const isEditable = !security?.securityTypeData?.isFuture

    const decimalAccruedValue =
      externalTransactionData.accruedAmount && security && clearwaterUnits
        ? Calculators.calculateAccruedInterest(
            security,
            externalTransactionData.accruedAmount,
            clearwaterUnits,
            Calculators.calculateCombinedFactor(security),
            true,
          ).toString()
        : '0.0'

    const dollarAccruedValue = externalTransactionData.accruedAmount
      ? Number(externalTransactionData.accruedAmount).toString()
      : '0.0'

    const factorValue = externalTransactionData.factor
      ? Number(externalTransactionData.factor).toString()
      : Calculators.calculateCombinedFactor(security).toString()

    const priceScalarValue =
      security?.securityMasterData.priceScalar.toString() ?? ''

    const contractSizeValue = security?.securityMasterData.contractSize
      ? security.securityMasterData.contractSize.toString()
      : '1.0'

    const proceedsValue =
      externalTransactionData.price && security && clearwaterUnits
        ? Calculators.calculateProceeds(
            externalTransactionData.price,
            Calculators.calculateAccruedInterest(
              security,
              externalTransactionData.accruedAmount ?? 0,
              clearwaterUnits,
              Calculators.calculateCombinedFactor(security),
              true,
            ),
            clearwaterUnits,
            externalTransactionData.commission ?? 0.0,
            externalTransactionData.originationFee ?? 0.0,
            externalTransactionData.drawFee ?? 0.0,
            externalTransactionData.exitFee ?? 0.0,
            externalTransactionData.exitFeeReceivable ?? 0.0,
            externalTransactionData.prepaymentPenaltyFee ?? 0.0,
            externalTransactionData.prepaidInterest ?? 0.0,
            externalTransactionData.interestDue ?? 0.0,
            (externalTransactionData.interestBearingInterestReserve ?? 0.0) +
              (externalTransactionData.interestBearingPrincipalReserve ?? 0.0) +
              (externalTransactionData.nonInterestBearingInterestReserve ??
                0.0) +
              (externalTransactionData.nonInterestBearingPrincipalReserve ??
                0.0),
            security,
            TransactionTypeEnum[externalTransactionData.tranType],
            Calculators.calculateCombinedFactor(security),
            this.applyHPL,
            InventoryType[
              externalTransactionData.inventoryType as keyof typeof InventoryType
            ],
          ).toString()
        : '0.0'

    const rowData = this.rowData.slice().map(te => {
      switch (te.id) {
        case 'decimalAccrued':
          return {
            ...te,
            isEditable: isEditable,
            value: decimalAccruedValue,
            [source]: decimalAccruedValue,
          }
        case 'dollarAccrued':
          return {
            ...te,
            isEditable: isEditable,
            value: dollarAccruedValue,
            [source]: dollarAccruedValue,
          }
        case 'factor':
          return {
            ...te,
            value: factorValue,
            [source]: factorValue,
          }
        case 'priceScalar':
          return {
            ...te,
            value: priceScalarValue,
            [source]: priceScalarValue,
          }
        case 'contractSize':
          return {
            ...te,
            value: contractSizeValue,
            [source]: contractSizeValue,
          }
        case 'proceeds':
          return {
            ...te,
            value: proceedsValue,
            [source]: externalTransactionData.amount
              ? externalTransactionData.amount.toString()
              : '',
          }
        case 'units':
          return {
            ...te,
            value: clearwaterUnits?.toString() ?? '',
          }
        default:
          return { ...te }
      }
    })
    await this.setRowData(rowData)
    this.loadAmortDetails(defaultBasis)
    this.updateSecurityInfoInTrade('thirdParty')
  }

  async createRowDataForHistoricalBalance(data: HistoricalBalanceData) {
    this.collateralBroker = data.collateralBroker
    const rowData = tradeEntryRecordToArray(
      createTradeEntryFromHistoricalBalance(data),
    )

    await this.setRowData(rowData)
    this.loadAccountDetails(this.accountId)
    this.loadSecurityOnCusipChange(data.assetId, data.date)
    this.setupInitialData(this.rowData)
    this.transactionLoaded = true
  }

  private async setRowData(rowData: TradeEntryModel[]) {
    if (this.gridApi == null) {
      this.rowData = rowData
      return
    }

    const updateRowNodeMap$ = new Subject()

    const onRowDataChanged = ((event: RowDataChangedEvent) => {
      event.api.forEachNode(node => {
        this.rowNodeMap.set(node.data['id'], node)
      })

      updateRowNodeMap$.next()
      updateRowNodeMap$.complete()
    }).bind(this)

    this.gridApi.api.addEventListener('rowDataChanged', onRowDataChanged)

    const updateRowNodeMapPromise = updateRowNodeMap$.toPromise()
    this.rowData = rowData
    await updateRowNodeMapPromise

    this.gridApi.api.removeEventListener('rowDataChanged', onRowDataChanged)
  }

  async plugExternalUnmatchedDataView(extTranId: number, accountId: number) {
    const externalDetailsPromise = this.createEditTransactionService
      .loadExternalTransactionDetail(extTranId, accountId)
      .toPromise()
    await Promise.all([externalDetailsPromise])
      .then(async ([externalTransactionDetail]) => {
        const externalTransactionData =
          externalTransactionDetail.externalTransaction
        this.hasTransactionMatchCustody =
          externalTransactionData.dataSourceTypeId == 1
        this.hasTransactionMatchTrading =
          externalTransactionData.dataSourceTypeId == 2
        this.accountDetails = externalTransactionDetail.accountDetails
        this.executionId = externalTransactionData.executionId
        this.externalAccountId = externalTransactionData.accountId
        this.originalCusip =
          externalTransactionData.cusip ?? externalTransactionData.alternateId
        this.collateralBroker =
          externalTransactionData.aggInfoJson?.collateralBroker
        this.externalTranTypeLabel =
          externalTransactionData.transactionTypeLabel

        if (
          this.accountDetails?.isAggregate &&
          this.accountDetails.accountId != this.externalAccountId
        ) {
          this.isExternalAccountSleeveSimple = true
          this.fetchSleeveSimples(this.accountDetails.accountId)
          await this.loadAndMapAccountDetailsForSleeve(this.externalAccountId)
        }

        this.accountName =
          this.accountDetails?.accountId +
          ' - ' +
          this.accountDetails?.accountName

        if (
          externalTransactionData.clearwaterCounterParty &&
          !this.counterPartyList.find(
            cp =>
              cp.name == externalTransactionData.clearwaterCounterParty?.name,
          )
        ) {
          this.counterPartyList.push(
            externalTransactionData.clearwaterCounterParty,
          )
        }
        const rowData = tradeEntryRecordToArray(
          externalTransactionData.dataSourceTypeId == 1
            ? createTradeEntryFromExternalTransactionCustody(
                externalTransactionData,
                this.accountName,
                this.isExternalAccountSleeveSimple,
                this.applyHPL,
              )
            : createTradeEntryFromExternalTransactionTrading(
                externalTransactionData,
                this.accountName,
                this.isExternalAccountSleeveSimple,
                this.applyHPL,
              ),
        )
        await this.setRowData(rowData)
        this.checkRowEligibilities()
        this.setupInitialData(this.rowData)

        const sourceType =
          externalTransactionData.dataSourceTypeId == 1 ? 'custody' : 'trading'

        const defaultBasis =
          externalTransactionDetail.accountDetails?.defaultBasis ?? 1

        if (
          externalTransactionData.securityId &&
          externalTransactionData.settleDate
        ) {
          // accountId should be set here; if it isn't, the backend treats the 0 as
          // "no account" and uses the global (clientId=1) security scope, not account 0.
          await this.createEditTransactionService
            .fetchSecurityDetailsFromSecurityId(
              externalTransactionData.securityId,
              externalTransactionData.settleDate,
              this.accountId ?? 0,
            )
            .toPromise()
            .then(security => {
              this.security = security
              this.updateSecurityBasedFields(
                externalTransactionData,
                defaultBasis,
                sourceType,
                security,
              )
            })
            .catch(err => {
              this.errorHeader = 'Error loading Security Details'
              this.errorDetails = err.error
            })
        } else {
          this.updateSecurityBasedFields(
            externalTransactionData,
            defaultBasis,
            sourceType,
          )
        }

        this.transactionLoaded = true
        this.setAccountDisplayName()
        this.dynamicRows = createDynamicRowsforExternalTransaction(
          externalTransactionData.aggInfoJson,
          sourceType,
        ) as TradeEntryModel[]
        if (this.gridApi) {
          this.validate(this.gridApi.api)
        }
      })
      .catch(err => {
        this.errorText = TransactionConstants.ERROR_LOADING_TRANSACTION
        this.errorDetails = err.error
      })
  }

  async plugEditTradeData(
    sessionId?: string,
    transactionId?: string,
    accountId?: number,
    securityId?: number,
  ): Promise<void> {
    if (transactionId && sessionId && accountId && securityId) {
      const params = new HttpParams()
        .set('accountId', accountId.toString())
        .set('securityId', securityId.toString())
      const tradeEntryTransactionDetails = this.createEditTransactionService
        .loadTransactionDetail(
          sessionId,
          transactionId,
          params,
          TranCategoryUrl.TRADE_ENTRY,
        )
        .toPromise()
      const transactionMatches = this.createEditTransactionService
        .loadTransactionMatches(transactionId)
        .toPromise()
      return tradeEntryTransactionDetails
        .then(async tradEntryTransactionDetails => {
          this.note.setValue(tradEntryTransactionDetails.note?.note ?? '')
          const trade =
            (tradEntryTransactionDetails.transactionData as Trade) ?? {}
          this.collateralBroker = trade.dataMap?.collateralBroker
          this.clearTransaction.patchValue(trade.clearedDate ? true : false)
          this.accountDetails = tradEntryTransactionDetails.accountDetails

          const security = {
            securityMasterData: tradEntryTransactionDetails.securityMasterData,
            securityTypeData: tradEntryTransactionDetails.securityTypeData,
            issuerTypeData: tradEntryTransactionDetails.issuerTypeData,
            decimalAccrued:
              tradEntryTransactionDetails.decimalAccrued ?? trade.accrued,
            securityIndexFactor:
              tradEntryTransactionDetails.securityIndexFactor,
            securityPrincipalFactor:
              tradEntryTransactionDetails.securityPrincipalFactor,
          } as unknown as Security
          this.originalTransaction = trade
          this.dataMap = this.originalTransaction.dataMap
          this.security = security

          const combinedFactor = Calculators.calculateCombinedFactor(security)

          this.gridApi?.api.refreshHeader()
          this.accountName =
            this.accountDetails?.accountId +
            ' - ' +
            this.accountDetails?.accountName
          if (this.accountDetailsAggregate)
            this.accountNameAgg =
              this.accountDetailsAggregate.accountId +
              ' - ' +
              this.accountDetailsAggregate.accountName

          if (
            trade.counterParty &&
            !this.counterPartyList.find(
              cp => cp.name == trade.counterParty?.name,
            )
          ) {
            this.counterPartyList.push(trade.counterParty)
          }

          const rowData = tradeEntryRecordToArray(
            editTradeEntryFromTransaction(
              trade,
              security,
              this.accountName,
              combinedFactor,
              this.applyHPL,
              this.accountNameAgg,
              this.tradingAccountName,
            ),
          )
          await this.setRowData(rowData)
          this.setupInitialData(this.rowData)
          this.updateSecurityInfoInTrade('thirdParty')
          this.checkRowEligibilities()
          this.transactionLoaded = true
          this.loadAmortDetails(
            tradEntryTransactionDetails.accountDetails?.defaultBasis ?? 1,
          )

          if (this.gridApi) {
            this.validate(this.gridApi.api)
          }

          transactionMatches.then(matches =>
            this.updateTransactionMatchFields(trade, security, matches),
          )
        })
        .catch(err => {
          this.errorText = TransactionConstants.ERROR_LOADING_TRANSACTION
          this.errorDetails = err.error
        })
    }
  }

  async updateTransactionMatchFields(
    trade: Trade,
    security: Security,
    matches: Map<string, TransactionMatch[]>,
  ) {
    this.transactionMatchesCustody = matches?.get(
      `${ExternalDataSourceTypeId.Custody}`,
    )
    this.transactionMatchesTrading = matches?.get(
      `${ExternalDataSourceTypeId.Trading}`,
    )
    let tradingAccountId = 0
    this.transactionMatchesTrading?.forEach(m => {
      tradingAccountId = m.accountId
    })

    if (trade.accountId !== tradingAccountId && tradingAccountId != 0) {
      await this.loadTradingAccountDetail(tradingAccountId)
    }
    if (this.accountDetails?.isAggregate) {
      this.accountDetailsAggregate = this.accountDetails
      await this.loadAndMapAccountDetailsForSleeve(trade.accountId)
    }

    this.hasTransactionMatchCustody = Boolean(this.transactionMatchesCustody)
    this.hasTransactionMatchTrading = Boolean(this.transactionMatchesTrading)

    const matchCustody = this.transactionMatchesCustody
      ? this.getConsolidatedMatch(
          JSON.parse(JSON.stringify(this.transactionMatchesCustody)),
        )
      : undefined
    const matchTrading = this.transactionMatchesTrading
      ? this.getConsolidatedMatch(
          JSON.parse(JSON.stringify(this.transactionMatchesTrading)),
        )
      : undefined
    if (
      this.transactionMatchesCustody &&
      this.transactionMatchesCustody.length > 1
    ) {
      const custodyColumnDef = this.gridApi?.api.getColumnDef('custody')
      if (custodyColumnDef) {
        custodyColumnDef.headerName = 'Custody (multi)'
      }
    }

    if (
      this.transactionMatchesTrading &&
      this.transactionMatchesTrading.length > 1
    ) {
      const tradingColumnDef = this.gridApi?.api.getColumnDef('trading')
      if (tradingColumnDef) {
        tradingColumnDef.headerName = 'Trading (multi)'
      }
    }

    if (
      matchCustody?.clearwaterCounterParty &&
      !this.counterPartyList.find(
        cp => cp.name == matchCustody?.clearwaterCounterParty?.name,
      )
    ) {
      this.counterPartyList.push(matchCustody?.clearwaterCounterParty)
    }
    if (
      matchTrading?.clearwaterCounterParty &&
      !this.counterPartyList.find(
        cp => cp.name == matchTrading?.clearwaterCounterParty?.name,
      )
    ) {
      this.counterPartyList.push(matchTrading?.clearwaterCounterParty)
    }

    const combinedFactor = Calculators.calculateCombinedFactor(security)
    const calculatedUnits = Calculators.calculateUnits(
      matchCustody ?? matchTrading,
      security,
      combinedFactor,
    )

    const updatedRowData = [...this.rowData].map(te => {
      switch (te.id) {
        case 'account':
          return {
            ...te,
            custody: matchCustody
              ? this.accountNameAgg ?? this.accountName
              : '',
            trading: matchTrading
              ? this.accountNameAgg ?? this.accountName
              : '',
          }
        case 'dataSource':
          return {
            ...te,
            custody: matchCustody?.dataSource ?? '',
            trading: matchTrading?.dataSource ?? '',
          }
        case 'transactionType':
          return {
            ...te,
            custody: matchCustody?.tranType
              ? TransactionTypeEnum[matchCustody.tranType]
              : '',
            trading: matchTrading?.tranType
              ? TransactionTypeEnum[matchTrading.tranType]
              : '',
          }
        case 'entryDate':
          return {
            ...te,
            custody: matchCustody?.entryDate,
            trading: matchTrading?.entryDate,
          }
        case 'settleDate':
          return {
            ...te,
            custody: matchCustody?.settleDate,
            trading: matchTrading?.settleDate,
          }
        case 'postDate':
          return {
            ...te,
            custody: matchCustody?.postDate,
            trading: matchTrading?.postDate,
          }
        case 'cusip':
          return {
            ...te,
            custody: matchCustody ? matchCustody.assetId : '',
            trading: matchTrading ? matchTrading.assetId : '',
          }
        case 'cwanIdentifier':
          return {
            ...te,
            custody: matchCustody ? matchCustody.cwanIdentifier ?? '' : '',
            trading: matchTrading ? matchTrading.cwanIdentifier ?? '' : '',
          }
        case 'price':
          return {
            ...te,
            custody: matchCustody?.price
              ? Number(matchCustody.price).toString()
              : '',
            trading: matchTrading?.price
              ? Number(matchTrading.price).toString()
              : '',
          }
        case 'units':
          return {
            ...te,
            custody: matchCustody?.units
              ? Number(matchCustody.units).toString()
              : '',
            trading: matchTrading?.units
              ? Number(matchTrading.units).toString()
              : '',
          }
        case 'commission':
          return {
            ...te,
            custody: matchCustody?.commission
              ? matchCustody?.commission.toString()
              : '',
            trading: matchTrading?.commission
              ? matchTrading?.commission.toString()
              : '',
          }
        case 'originationFee':
          return {
            ...te,
            custody: matchCustody?.originationFee
              ? matchCustody?.originationFee.toString()
              : '',
            trading: matchTrading?.originationFee
              ? matchTrading?.originationFee.toString()
              : '',
          }
        case 'exitFee':
          return {
            ...te,
            custody: matchCustody?.exitFee
              ? matchCustody?.exitFee.toString()
              : '',
            trading: matchTrading?.exitFee
              ? matchTrading?.exitFee.toString()
              : '',
          }
        case 'exitFeeReceivable':
          return {
            ...te,
            custody: matchCustody?.exitFeeReceivable
              ? matchCustody?.exitFeeReceivable.toString()
              : '',
            trading: matchTrading?.exitFeeReceivable
              ? matchTrading?.exitFeeReceivable.toString()
              : '',
          }
        case 'drawFee':
          return {
            ...te,
            custody: matchCustody?.drawFee
              ? matchCustody?.drawFee.toString()
              : '',
            trading: matchTrading?.drawFee
              ? matchTrading?.drawFee.toString()
              : '',
          }
        case 'prepaymentPenaltyFee':
          return {
            ...te,
            custody: matchCustody?.prepaymentPenaltyFee
              ? matchCustody?.prepaymentPenaltyFee.toString()
              : '',
            trading: matchTrading?.prepaymentPenaltyFee
              ? matchTrading?.prepaymentPenaltyFee.toString()
              : '',
          }
        case 'prepaidInterest':
          return {
            ...te,
            custody: matchCustody?.prepaidInterest
              ? matchCustody?.prepaidInterest.toString()
              : '',
            trading: matchTrading?.prepaidInterest
              ? matchTrading?.prepaidInterest.toString()
              : '',
          }
        case 'interestDue':
          return {
            ...te,
            custody: matchCustody?.interestDue
              ? matchCustody?.interestDue.toString()
              : '',
            trading: matchTrading?.interestDue
              ? matchTrading?.interestDue.toString()
              : '',
          }
        case 'decimalAccrued':
          return {
            ...te,
            custody: matchCustody
              ? matchCustody.accruedAmount
                ? Calculators.calculateAccruedInterest(
                    security,
                    matchCustody.accruedAmount,
                    calculatedUnits,
                    combinedFactor,
                    true,
                  ).toString()
                : '0.0'
              : '',
            trading: matchTrading
              ? matchTrading.accruedAmount
                ? Calculators.calculateAccruedInterest(
                    security,
                    matchTrading.accruedAmount,
                    calculatedUnits,
                    combinedFactor,
                    true,
                  ).toString()
                : '0.0'
              : '',
          }
        case 'dollarAccrued':
          return {
            ...te,
            custody: matchCustody?.accruedAmount
              ? Number(matchCustody.accruedAmount).toString()
              : '',
            trading: matchTrading?.accruedAmount
              ? Number(matchTrading.accruedAmount).toString()
              : '',
          }
        case 'factor':
          return {
            ...te,
            custody: matchCustody
              ? matchCustody.factor?.toString() ?? combinedFactor.toString()
              : '',
            trading: matchTrading
              ? matchTrading.factor?.toString() ?? combinedFactor.toString()
              : '',
          }
        case 'priceScalar':
          return {
            ...te,
            custody: matchCustody
              ? security.securityMasterData?.priceScalar.toString()
              : '',
            trading: matchTrading
              ? security.securityMasterData?.priceScalar.toString()
              : '',
          }
        case 'proceeds':
          return {
            ...te,
            custody: matchCustody?.amount ? matchCustody.amount.toString() : '',
            trading: matchTrading?.amount ? matchTrading.amount.toString() : '',
          }
        case 'counterParty':
          return {
            ...te,
            custody: matchCustody ? matchCustody.counterParty ?? 'Unknown' : '',
            trading: matchTrading ? matchTrading.counterParty ?? 'Unknown' : '',
          }
        case 'clearwaterCounterParty':
          return {
            ...te,
            custody: matchCustody
              ? matchCustody.clearwaterCounterParty?.name ?? 'Unknown'
              : '',
            trading: matchTrading
              ? matchTrading.clearwaterCounterParty?.name ?? 'Unknown'
              : '',
          }
        case 'transTypeCode':
          return {
            ...te,
            custody: matchCustody ? trade.tranType : '',
            trading: matchTrading ? trade.tranType : '',
          }
      }
      return te
    })
    await this.setRowData(updatedRowData)

    if (
      this.transactionMatchesCustody &&
      this.transactionMatchesCustody.length > 1
    ) {
      this.removeExtraRowData('custody')
    }

    if (
      this.transactionMatchesTrading &&
      this.transactionMatchesTrading.length > 1
    ) {
      this.removeExtraRowData('trading')
    }
    this.generateDynamicRows(matchTrading, matchCustody)
  }

  async loadTradingAccountDetail(tradingAccountId?: number) {
    if (tradingAccountId) {
      const tradingAccountDetailPromise$ =
        this.createEditTransactionService.loadAccountDetails(tradingAccountId)
      return Promise.all([tradingAccountDetailPromise$.toPromise()]).then(
        async ([tradingAccountDetail]) => {
          this.tradingAccountName =
            tradingAccountDetail.accountId +
            '-' +
            tradingAccountDetail.accountName
        },
      )
    }
  }
  async removeExtraRowData(source: 'custody' | 'trading') {
    this.rowData.forEach(row => {
      if (!this.consolidatedFields.has(row.id)) {
        row[source] = ''
      }
    })
  }

  getConsolidatedMatch(
    transactionMatches: TransactionMatch[],
  ): TransactionMatch {
    let commission = 0.0
    let units = 0.0
    let accruedAmount = 0.0
    let amount = 0.0
    let originationFee = 0.0
    let exitFee = 0.0
    let exitFeeReceivable = 0.0
    let drawFee = 0.0
    let prepaymentPenaltyFee = 0.0
    let prepaidInterest = 0.0
    let interestDue = 0.0

    transactionMatches.forEach(tranMatch => {
      amount += tranMatch.amount
      if (tranMatch.commission) commission += tranMatch.commission
      if (tranMatch.units) units += tranMatch.units
      if (tranMatch.accruedAmount) accruedAmount += tranMatch.accruedAmount
      if (tranMatch.originationFee) originationFee += tranMatch.originationFee
      if (tranMatch.exitFee) exitFee += tranMatch.exitFee
      if (tranMatch.exitFeeReceivable)
        exitFeeReceivable += tranMatch.exitFeeReceivable
      if (tranMatch.drawFee) drawFee += tranMatch.drawFee
      if (tranMatch.prepaymentPenaltyFee)
        prepaymentPenaltyFee += tranMatch.prepaymentPenaltyFee
      if (tranMatch.prepaidInterest)
        prepaidInterest += tranMatch.prepaidInterest
      if (tranMatch.interestDue) interestDue += tranMatch.interestDue
    })
    const matchConsolidated = transactionMatches[0]
    matchConsolidated.amount = amount
    matchConsolidated.commission = commission
    matchConsolidated.originationFee = originationFee
    matchConsolidated.exitFee = exitFee
    matchConsolidated.exitFeeReceivable = exitFeeReceivable
    matchConsolidated.drawFee = drawFee
    matchConsolidated.prepaymentPenaltyFee = prepaymentPenaltyFee
    matchConsolidated.prepaidInterest = prepaidInterest
    matchConsolidated.interestDue = interestDue
    matchConsolidated.accruedAmount = accruedAmount
    matchConsolidated.units = units
    return matchConsolidated
  }

  editInlineColumns(params: ICellEditorParams): InlineEditorParams {
    if (
      params.data.id === 'postDate' ||
      params.data.id === 'settleDate' ||
      params.data.id === 'entryDate'
    ) {
      return {
        dataType: InlineEditDataType.Date,
      }
    } else if (params.data.id === 'amortTargetDate') {
      return {
        dataType: InlineEditDataType.Date,
      }
    } else if (
      params.data.id === 'transactionType' ||
      params.data.id === 'transTypeCode'
    ) {
      return {
        dataType: InlineEditDataType.Select,
        options: {
          items: this.transTypes,
        },
      }
    } else if (params.data.id === 'inventoryType') {
      return {
        dataType: InlineEditDataType.Select,
        options: {
          items: Object.keys(InventoryType),
        },
      }
    } else if (params.data.id === 'price' || params.data.id === 'units') {
      return {
        dataType: InlineEditDataType.Number,
        options: {
          format: this.formatOption,
        },
        disablePopover: true,
      }
    } else if (params.data.id === 'cusip') {
      return {
        dataType: InlineEditDataType.String,
        disablePopover: true,
      }
    } else if (
      params.data.id === 'decimalAccrued' ||
      params.data.id === 'dollarAccrued' ||
      params.data.id === 'amortTargetPrice' ||
      params.data.id === 'priceScalar' ||
      params.data.id === 'proceeds'
    ) {
      return {
        dataType: InlineEditDataType.Number,
        options: {
          format: this.formatOption,
        },
        disablePopover: true,
      }
    } else if (
      params.data.id === 'contractSize' ||
      params.data.id === 'commission' ||
      params.data.id === 'factor' ||
      params.data.id === 'originationFee' ||
      params.data.id === 'exitFee' ||
      params.data.id === 'exitFeeReceivable' ||
      params.data.id === 'drawFee' ||
      params.data.id === 'prepaymentPenaltyFee' ||
      params.data.id === 'prepaidInterest' ||
      params.data.id === 'interestDue' ||
      params.data.id === 'interestBearingInterestReserve' ||
      params.data.id === 'interestBearingPrincipalReserve' ||
      params.data.id === 'nonInterestBearingInterestReserve' ||
      params.data.id === 'nonInterestBearingPrincipalReserve'
    ) {
      return {
        dataType: InlineEditDataType.Number,
        disablePopover: true,
        options: { format: this.formatOption },
      }
    } else if (params.data.id === 'clearwaterCounterParty') {
      return {
        dataType: InlineEditDataType.Select,
        options: this.getCounterPartyOptions(),
      }
    } else if (params.data.id === 'account') {
      return {
        dataType: InlineEditDataType.Select,
        options: {
          items: this.sleeveSimpleAccount,
          bindValue: 'displayLabel',
          bindLabel: 'displayLabel',
          loading: this.sleeveSimpleAccount.length == 0,
        },
      }
    } else if (params.data.id === 'drawType') {
      return {
        dataType: InlineEditDataType.Select,
        options: {
          items: this.drawTypes,
        },
      }
    } else if (params.data.id === 'reserveBalanceField') {
      return {
        dataType: InlineEditDataType.Select,
        options: {
          items: this.reserveBalanceFieldOptions,
        },
      }
    } else if (params.data.id === 'buyType') {
      return {
        dataType: InlineEditDataType.Select,
        options: {
          items: this.buyTypes,
        },
      }
    }

    return {
      dataType: InlineEditDataType.String,
      disablePopover: true,
    }
  }

  /**
   * PVAL-25738: external users must not see the internal Asset ID row when
   * editing/viewing an existing or external transaction.
   */
  private get hideInternalAssetIdRows(): boolean {
    return (
      !this.isInternalUser &&
      (isEditModal(this.transInput) || isExternalTransaction(this.transInput))
    )
  }

  private createGridOptions(): GridOptions {
    return {
      ...heliosSizingAdjustments,
      ...internalAssetIdFilterGridOptions(
        () => this.hideInternalAssetIdRows,
        () => this.rowNodeMap,
      ),
      onGridReady: event => this.onGridReady(event),
      defaultColDef: {
        ...heliosDefaultColDef,
        menuTabs: [],
        minWidth: heliosDefaultColWidth.ValidityStatusCol,
        sortable: false,
      },
      sideBar: false,
      enableCellChangeFlash: true,
      enableRangeSelection: true,
      suppressMultiRangeSelection: false,
      enableFillHandle: true,
      fillHandleDirection: 'y',
      onCellEditingStarted: () => {
        this.isCellEditing = true
      },
      onCellEditingStopped: () => {
        this.isCellEditing = false
      },
      onCellKeyDown: (event: CellKeyDownEvent) => {
        if (event.api.getEditingCells().length == 0) {
          emptySelectedCellsOnValidKeyPress(
            (event.event as KeyboardEvent).key,
            event.api,
          )
          const categoryNode = this.rowNodeMap.get('transactionType')
          if (categoryNode?.data.value == '') {
            handleValidation(categoryNode, 'INVALID', 'Valid Value Required')
            this.isDataValid = false
          }
        }
      },
      getRowNodeId: ({ id }: TradeEntryModel) => id,
      columnDefs: this.colDefs,
      onCellDoubleClicked: (event: CellDoubleClickedEvent) => {
        if (
          event.column.getColId() === 'custody' ||
          event.column.getColId() === 'trading' ||
          event.column.getColId() === 'thirdParty'
        ) {
          const rowNode = event.api.getRowNode(event.data['id'])
          if (
            event.data[event.column.getColId()] !== '' &&
            rowNode?.data.isEditable
          ) {
            if (rowNode.data['id'] === 'clearwaterCounterParty') {
              //if counterParty is unmatched double click should do nothing
              if (
                event.data[event.column.getColId()] !==
                'Unmatched Counter Party'
              ) {
                rowNode?.setDataValue(
                  'value',
                  event.data[event.column.getColId()],
                )
              }
            } else {
              rowNode?.setDataValue(
                'value',
                event.data[event.column.getColId()],
              )
            }
          }
          if (rowNode != null) {
            event.api.redrawRows({ rowNodes: [rowNode] })
          }
        } else if (event.column.getColId() === 'value') {
          const element = event.event?.target as HTMLElement
          if (element.className == 'ag-fill-handle') {
            fillHandleDoubleClickHandlerSingleDialog(event.api)
          } else {
            event.api.startEditingCell({
              rowIndex: event.rowIndex ?? 0,
              colKey: event.column.getColId(),
            })
          }
        }
      },
      onCellValueChanged: (event: CellValueChangedEvent) => {
        this.onColumnValueChanged(event)
      },
    }
  }

  onGridReady(event: GridReadyEvent) {
    this.gridApi = event
    this.init().then(() => {
      const now = Date.now()
      const type = singleDialogMetricsDetail(this.transInput)
      this.telemetryService.emitCustomEvent(SINGLE_DIALOG_EVENT_NAME, {
        dialogName: 'Trade-' + type,
        durationMs: now - this.startTime,
      })
    })
    resizeColumns(event.api, event.columnApi)
    this.copyService.registerGrid(event.api)
    this.pasteService.registerGrid(event.api, event.columnApi)
  }

  get colDef() {
    return this.colDefs
  }

  private getColDefs(): ColDef[] {
    return [
      {
        field: tradeField('isEditable'),
        hide: true,
      },
      {
        field: tradeField('errorMessage'),
        hide: true,
      },
      {
        field: tradeField('validityStatus'),
        headerName: '',
        valueGetter: () => '',
        width: heliosDefaultColWidth.ValidityStatusCol,
        cellClass: params => {
          return params.data.validityStatus === 'valid'
            ? 'mdi mdi-check-circle text-success'
            : params.data.validityStatus === 'invalid'
            ? 'mdi mdi-close-circle-outline text-danger'
            : 'mdi mdi-alert-circle-outline text-warning'
        },
        tooltipField: 'errorMessage',
        tooltipComponentParams: {
          showTooltip: params =>
            params?.data.errorMessage != null &&
            params.data.errorMessage !== '',
        } as NfTooltipParams,
      },
      {
        field: tradeField('field'),
        headerName: 'Field',
        width: 110,
      },
      {
        field: tradeField('value'),
        headerName: 'Value',
        type: getInlineEditColumnType(),
        cellEditorParams: (params: ICellEditorParams) =>
          this.editInlineColumns(params),
        editable: (x: EditableCallbackParams) => this.editable(x),
        width: 300,
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        valueFormatter: params =>
          formatNumberWithThousandSeparator(params.data.type, params.value),
      },
      {
        field: tradeField('trading'),
        headerName: 'Trading',
        width: 230,
        cellStyle: (params: CellClassParams) =>
          this.deduceValidityStatusCellClass(
            params,
            this.transactionMatchesTrading &&
              this.transactionMatchesTrading?.length > 1
              ? true
              : false,
          ),
        valueFormatter: params =>
          formatNumberWithThousandSeparator(params.data.type, params.value),
      },
      {
        field: tradeField('custody'),
        headerName: 'Custody',
        suppressMenu: false,
        menuTabs: ['generalMenuTab'],
        width: 230,
        cellStyle: (params: CellClassParams) =>
          this.deduceValidityStatusCellClass(
            params,
            this.transactionMatchesCustody &&
              this.transactionMatchesCustody?.length > 1
              ? true
              : false,
          ),
        valueFormatter: params =>
          formatNumberWithThousandSeparator(params.data.type, params.value),
      },
      {
        field: tradeField('thirdParty'),
        headerName: 'Third Party',
        width: 150,
        valueFormatter: params =>
          formatNumberWithThousandSeparator(params.data.type, params.value),
      },
    ]
  }

  loadSecurityOnCusipChange(cusip: string, settleDate: string) {
    // The cusip lookup is account-scoped; defer it until an account is picked.
    if (this.aggregateAccountUnresolved) return
    this.transactionLoaded = false
    this.loadingText = TransactionConstants.LOAD_SECURITY_INFORMATION
    // accountId should be set here; if it isn't, the backend treats the 0 as
    // "no account" and uses the global (clientId=1) security scope, not account 0.
    this.createEditTransactionService
      .fetchSecurityDetailsFromCusip(cusip, settleDate, this.accountId ?? 0)
      .subscribe(
        security => {
          this.security = security
          if (!isEditModal(this.transInput)) {
            this.updateSecurityInfoInTrade('value')
            this.rowNodeMap
              .get('decimalAccrued')
              ?.setDataValue(
                'isEditable',
                !security?.securityTypeData?.isFuture,
              )
            this.rowNodeMap
              .get('dollarAccrued')
              ?.setDataValue(
                'isEditable',
                !security?.securityTypeData?.isFuture,
              )
          }

          this.updateSecurityInfoInTrade('thirdParty')
          this.checkColumnEligibility(
            'exitFee',
            ['Buy', 'Maturity', 'Principal Repayment'],
            'commission',
          )
          this.checkColumnEligibility(
            'exitFeeReceivable',
            ['Buy', 'Sell'],
            'commission',
          )
          this.checkColumnEligibility('drawFee', ['Draw'], 'commission')
          this.checkColumnEligibility(
            'prepaymentPenaltyFee',
            ['Principal Repayment', 'Maturity'],
            'exitFee',
          )
          this.checkColumnEligibility(
            'originationFee',
            ['Buy', 'Draw'],
            'commission',
          )
          this.checkColumnEligibility(
            'prepaidInterest',
            ['Buy', 'Draw'],
            'originationFee',
          )
          this.checkColumnEligibility('interestDue', ['Buy'], 'prepaidInterest')
          this.checkColumnEligibility(
            'interestBearingInterestReserve',
            ['Buy', 'Sell'],
            'exitFeeReceivable',
          )
          this.checkColumnEligibility(
            'interestBearingPrincipalReserve',
            ['Buy', 'Sell'],
            'interestBearingInterestReserve',
          )
          this.checkColumnEligibility(
            'nonInterestBearingInterestReserve',
            ['Buy', 'Sell'],
            'interestBearingPrincipalReserve',
          )
          this.checkColumnEligibility(
            'nonInterestBearingPrincipalReserve',
            ['Buy', 'Sell'],
            'nonInterestBearingInterestReserve',
          )

          this.adjustProceeds()
          if (this.accountDetails)
            this.loadAmortDetails(this.accountDetails?.defaultBasis ?? 1)
          this.transactionLoaded = true
        },
        err => {
          this.errorHeader = TransactionConstants.ERROR_LOADING_SECURITY
          this.errorDetails = err.error
          this.transactionLoaded = true
          this.gridApi?.api.getRowNode('cusip')?.setDataValue('value', '')
        },
      )
  }

  updateSecurityInfoInTrade(
    column: 'value' | 'thirdParty' | 'custody' | 'trading',
  ) {
    if (this.security) {
      const combinedFactor = Calculators.calculateCombinedFactor(this.security)
      this.rowNodeMap
        .get('priceScalar')
        ?.setDataValue(column, this.security?.securityMasterData.priceScalar)

      this.rowNodeMap
        .get('contractSize')
        ?.setDataValue(
          column,
          this.security?.securityMasterData.contractSize
            ? this.security?.securityMasterData.contractSize
            : 1.0,
        )
      this.rowNodeMap.get('factor')?.setDataValue(column, combinedFactor)
      this.rowNodeMap
        .get('decimalAccrued')
        ?.setDataValue(column, this.security.decimalAccrued)

      const units = this.rowNodeMap.get('units')?.data.value
      const factor = this.rowNodeMap.get('factor')?.data.value
      this.calculateDollarAccrued(
        column,
        units,
        factor !== '' ? factor : combinedFactor,
      )
    }
  }

  calculateDollarAccrued(
    column: 'value' | 'thirdParty' | 'custody' | 'trading',
    units: number,
    factor: number,
  ) {
    if (this.security) {
      const dollarAccruedCalculated = Calculators.calculateAccruedAmount(
        this.security,
        Number(this.security.decimalAccrued),
        Number(units),
        factor,
      )
      this.rowNodeMap
        .get('dollarAccrued')
        ?.setDataValue(column, dollarAccruedCalculated)
    }
  }

  onColumnValueChanged(event: CellValueChangedEvent) {
    if (
      event.newValue === event.oldValue ||
      event.column.getColId() != 'value'
    ) {
      return
    }
    const rowNode = event.api.getRowNode(event.data.id)

    const tradeEntryRow: TradeEntryModel = event.data

    /* Category onChange event handling */
    switch (tradeEntryRow.id) {
      case 'transactionType':
        this.adjustForTransactionTypeChange(event)
        this.adjustExitFeeReceivable()
        this.adjustProceeds()
        this.advancedRowDisplay()
        this.loadAmortDetails(this.accountDetails?.defaultBasis ?? 1)
        break
      case 'price':
        this.adjustProceeds()
        this.loadAmortDetails(this.accountDetails?.defaultBasis ?? 1)
        break
      case 'units':
        this.adjustDollarAccrued()
        this.adjustProceeds()
        this.adjustUnitsAndRelatedFieldsForTransType(
          this.rowNodeMap.get('transTypeCode')?.data.value,
        )
        this.loadAmortDetails(this.accountDetails?.defaultBasis ?? 1)
        break
      case 'entryDate':
        this.loadAmortDetails(this.accountDetails?.defaultBasis ?? 1)
        break
      case 'postDate':
        this.clearUnclearTransaction(event.data.value, tradeEntryRow.id)
        this.loadAmortDetails(this.accountDetails?.defaultBasis ?? 1)
        break
      case 'settleDate':
        this.clearUnclearTransaction(event.data.value, tradeEntryRow.id)
        break
      case 'commission':
        this.adjustProceeds()
        this.loadAmortDetails(this.accountDetails?.defaultBasis ?? 1)
        break
      case 'originationFee':
        this.adjustProceeds()
        break
      case 'exitFee':
        this.adjustProceeds()
        break
      case 'exitFeeReceivable':
        this.adjustExitFeeReceivable()
        this.adjustProceeds()
        break
      case 'drawFee':
        this.adjustProceeds()
        break
      case 'prepaymentPenaltyFee':
        this.adjustProceeds()
        break
      case 'prepaidInterest':
        this.adjustProceeds()
        break
      case 'interestDue':
        this.adjustProceeds()
        break
      case 'interestBearingInterestReserve':
        this.adjustProceeds()
        break
      case 'interestBearingPrincipalReserve':
        this.adjustProceeds()
        break
      case 'nonInterestBearingInterestReserve':
        this.adjustProceeds()
        break
      case 'nonInterestBearingPrincipalReserve':
        this.adjustProceeds()
        break
      case 'inventoryType':
        this.adjustProceeds()
        this.adjustUnitsAndRelatedFieldsForTransType(
          this.rowNodeMap.get('transTypeCode')?.data.value,
        )
        break
      case 'decimalAccrued':
        this.adjustDollarAccrued()
        this.adjustProceeds()
        break
      case 'dollarAccrued':
        this.adjustDecimalAccrued()
        this.adjustProceeds()
        break
      case 'factor':
        if (!this.security?.securityTypeData?.isBond) {
          rowNode?.setDataValue('value', 1.0)
        }
        this.adjustDollarAccrued()
        this.adjustProceeds()
        break
      case 'account':
        this.loadAggAccountDetails(event.data.value)
        break
      case 'drawType':
        this.setDataMapFields(event.data.value)
        break
      case 'reserveBalanceField':
        this.setReserveBalanceField(event.data.value)
        break
      case 'buyType':
        this.setDataMapFields(event.data.value)
        break
    }
    if (event.data.id != 'transactionType' && event.data.id != 'account') {
      if (this.aggregateAccountUnresolved) {
        // No account yet: flag the edited field instead of validating it.
        addWarningDetails(event.node, ACCOUNT_REQUIRED_FIELD_WARNING)
        event.api.refreshCells({ force: true, rowNodes: [event.node] })
      } else {
        this.validate(
          event.api,
          event.data.id == 'cusip' || event.data.id == 'settleDate',
        )
      }
    }
  }

  setupInitialData(rowData: TradeEntryModel[]) {
    rowData.forEach(element => {
      if (element.validityStatus == 'invalid') {
        this.errorFields.push(element.id)
        this.isDataValid = false
      }
      const tableNode = this.rowNodeMap.get(element.id)
      if (tableNode) {
        if (
          tableNode.data.validityStatus == 'invalid' &&
          element.validityStatus == 'valid'
        ) {
          this.errorFields.splice(
            this.errorFields.indexOf(tableNode.data['id']),
            1,
          )
          if (this.errorFields.length == 0) {
            this.isDataValid = true
          }
        }
        tableNode.setData(element)
      }
    })
  }

  adjustInventoryType() {
    if (this.rowNodeMap.get('inventoryType')?.data.value !== '') {
      if (
        TransactionType_FOR_SHORT.includes(
          this.rowNodeMap.get('transactionType')?.data.value,
        )
      ) {
        this.rowNodeMap.get('inventoryType')?.setDataValue('value', 'SHORT')
      } else if (
        this.rowNodeMap.get('transactionType')?.data.value ==
        TransactionTypeEnum.OPTION_WRITE
      ) {
        this.rowNodeMap.get('inventoryType')?.setDataValue('value', 'ISSUE')
      } else {
        this.rowNodeMap.get('inventoryType')?.setDataValue('value', 'LONG')
      }
    }
  }

  adjustDollarAccrued() {
    const accruedInterest = this.rowNodeMap.get('decimalAccrued')?.data.value
    const accruedInterestCalculated =
      this.rowNodeMap.get('decimalAccrued')?.data.thirdParty
    const units = this.rowNodeMap.get('units')?.data.value
    const factor = this.rowNodeMap.get('factor')?.data.value
    if (
      this.security &&
      units !== '' &&
      accruedInterest !== '' &&
      accruedInterestCalculated !== '' &&
      factor !== ''
    ) {
      const dollarAccrued = Calculators.calculateAccruedAmount(
        this.security,
        Number(accruedInterest),
        Number(units),
        Number(factor),
      )
      const dollarAccruedCalculated = Calculators.calculateAccruedAmount(
        this.security,
        Number(accruedInterestCalculated),
        Number(units),
        Number(factor),
      )
      if (
        dollarAccrued.toFixed(10) !=
        Number(this.rowNodeMap.get('dollarAccrued')?.data.value).toFixed(10)
      ) {
        this.rowNodeMap
          .get('dollarAccrued')
          ?.setDataValue('value', dollarAccrued)
      }

      if (
        dollarAccruedCalculated !=
        this.rowNodeMap.get('dollarAccrued')?.data.thirdParty
      ) {
        this.rowNodeMap
          .get('dollarAccrued')
          ?.setDataValue('thirdParty', dollarAccruedCalculated)
      }
    }
  }

  adjustDecimalAccrued() {
    const accruedAmount = this.rowNodeMap.get('dollarAccrued')?.data.value
    const units = this.rowNodeMap.get('units')?.data.value
    const factor = this.rowNodeMap.get('factor')?.data.value
    if (
      this.security &&
      units !== '' &&
      accruedAmount !== '' &&
      factor !== ''
    ) {
      const decimalAccrued = Calculators.calculateAccruedInterest(
        this.security,
        Number(accruedAmount),
        Number(units),
        Number(factor),
        false,
      )
      if (
        decimalAccrued.toFixed(10) !=
        Number(this.rowNodeMap.get('decimalAccrued')?.data.value).toFixed(10)
      ) {
        this.rowNodeMap
          .get('decimalAccrued')
          ?.setDataValue('value', decimalAccrued)
      }
    }
  }

  private adjustProceeds() {
    const price = this.rowNodeMap.get('price')?.data.value
    if (price && price !== '') {
      const commission = Number(this.rowNodeMap.get('commission')?.data.value)
      const originationFee = this.rowNodeMap.get('originationFee')?.data.value
        ? Number(this.rowNodeMap.get('originationFee')?.data.value)
        : 0.0
      const drawFee = this.rowNodeMap.get('drawFee')?.data.value
        ? Number(this.rowNodeMap.get('drawFee')?.data.value)
        : 0.0
      const exitFee =
        this.rowNodeMap.get('exitFee')?.data.value &&
        this.rowNodeMap.get('transTypeCode')?.data.value !== 'BUY'
          ? Number(this.rowNodeMap.get('exitFee')?.data.value)
          : 0.0
      const exitFeeReceivable = this.rowNodeMap.get('exitFeeReceivable')?.data
        .value
        ? Number(this.rowNodeMap.get('exitFeeReceivable')?.data.value)
        : 0.0
      const prepaymentPenaltyFee = this.rowNodeMap.get('prepaymentPenaltyFee')
        ?.data.value
        ? Number(this.rowNodeMap.get('prepaymentPenaltyFee')?.data.value)
        : 0.0
      const prepaidInterest = this.rowNodeMap.get('prepaidInterest')?.data.value
        ? Number(this.rowNodeMap.get('prepaidInterest')?.data.value)
        : 0.0
      const interestDue = this.rowNodeMap.get('interestDue')?.data.value
        ? Number(this.rowNodeMap.get('interestDue')?.data.value)
        : 0.0
      const reserveTotal =
        (this.rowNodeMap.get('interestBearingInterestReserve')?.data.value
          ? Number(
              this.rowNodeMap.get('interestBearingInterestReserve')?.data.value,
            )
          : 0.0) +
        (this.rowNodeMap.get('interestBearingPrincipalReserve')?.data.value
          ? Number(
              this.rowNodeMap.get('interestBearingPrincipalReserve')?.data
                .value,
            )
          : 0.0) +
        (this.rowNodeMap.get('nonInterestBearingInterestReserve')?.data.value
          ? Number(
              this.rowNodeMap.get('nonInterestBearingInterestReserve')?.data
                .value,
            )
          : 0.0) +
        (this.rowNodeMap.get('nonInterestBearingPrincipalReserve')?.data.value
          ? Number(
              this.rowNodeMap.get('nonInterestBearingPrincipalReserve')?.data
                .value,
            )
          : 0.0)
      const accruedInterest =
        this.rowNodeMap.get('decimalAccrued')?.data.value === ''
          ? 0.0
          : Number(this.rowNodeMap.get('decimalAccrued')?.data.value)
      const units =
        this.rowNodeMap.get('units')?.data.value === ''
          ? 1.0
          : Number(this.rowNodeMap.get('units')?.data.value)
      const factor =
        this.rowNodeMap.get('factor')?.data.value === ''
          ? 1.0
          : Number(this.rowNodeMap.get('factor')?.data.value)
      const inventoryTypeKey: string =
        this.rowNodeMap.get('inventoryType')?.data.value === ''
          ? 'LONG'
          : this.rowNodeMap.get('inventoryType')?.data.value
      const transactionType: TransactionTypeEnum =
        TransactionTypeEnum[
          this.rowNodeMap.get('transTypeCode')?.data.value as TransactionTypeKey
        ]
      let proceedValue = 0.0
      if (!this.security) {
        proceedValue = Calculators.calculateProceedsWithAssumptions(
          Number(price),
          accruedInterest,
          units,
          commission,
          originationFee,
          drawFee,
          exitFee,
          exitFeeReceivable,
          prepaymentPenaltyFee,
          prepaidInterest,
          interestDue,
          reserveTotal,
          factor,
          this.applyHPL,
        )
      } else {
        proceedValue = Calculators.calculateProceeds(
          Number(price),
          accruedInterest,
          units,
          commission,
          originationFee,
          drawFee,
          exitFee,
          exitFeeReceivable,
          prepaymentPenaltyFee,
          prepaidInterest,
          interestDue,
          reserveTotal,
          this.security,
          transactionType,
          factor,
          this.applyHPL,
          inventoryTypeKey,
        )
      }
      if (proceedValue != this.rowNodeMap.get('proceeds')?.data.value) {
        this.rowNodeMap.get('proceeds')?.setDataValue('value', proceedValue)
      }
    }
  }

  private adjustForTransactionTypeChange(event: CellValueChangedEvent) {
    const transType = Object.keys(TransactionTypeEnum).filter(
      x => TransactionTypeEnum[x as TransactionTypeKey] == event.data['value'],
    )[0]

    const transTypeCodeNode = this.rowNodeMap.get('transTypeCode')
    if (transTypeCodeNode) {
      transTypeCodeNode?.setDataValue('value', transType)
    }
    this.adjustInventoryType()
    this.adjustUnitsAndRelatedFieldsForTransType(
      transType as TransactionTypeKey,
    )
  }
  private adjustUnitsAndRelatedFieldsForTransType(
    transType: TransactionTypeKey,
  ) {
    let inventoryType = this.rowNodeMap.get('inventoryType')?.data.value
    if (!inventoryType) {
      inventoryType = getValidInventory(transType)
    }
    this.updateUnitsAndRelatedField(transType, inventoryType)
  }

  private updateUnitsAndRelatedField(
    transType: TransactionTypeKey,
    inventoryType: string,
  ) {
    if (
      ENTRIES.includes(TransactionTypeEnum[transType]) &&
      EXITS.includes(TransactionTypeEnum[transType])
    ) {
      this.adjustPositiveUnits()
    } else if (ENTRIES.includes(TransactionTypeEnum[transType])) {
      if (inventoryType === 'LONG' || inventoryType === 'Long Position') {
        this.adjustPositiveUnits()
      } else if (
        inventoryType === 'SHORT' ||
        inventoryType === 'Short Position'
      ) {
        this.adjustNegativeUnits()
      }
    } else if (EXITS.includes(TransactionTypeEnum[transType])) {
      if (inventoryType === 'LONG' || inventoryType === 'Long Position') {
        this.adjustNegativeUnits()
      } else if (
        inventoryType === 'SHORT' ||
        inventoryType === 'Short Position'
      ) {
        this.adjustPositiveUnits()
      }
    }
  }

  adjustNegativeUnits() {
    if (
      this.rowNodeMap.get('dollarAccrued')?.data.value !== '' &&
      Number(this.rowNodeMap.get('dollarAccrued')?.data.value) < 0
    ) {
      this.rowNodeMap
        .get('dollarAccrued')
        ?.setDataValue(
          'value',
          Math.abs(this.rowNodeMap.get('dollarAccrued')?.data.value),
        )
      this.rowNodeMap
        .get('dollarAccrued')
        ?.setDataValue(
          'thirdParty',
          -1 * Math.abs(this.rowNodeMap.get('dollarAccrued')?.data.value),
        )
    }
    if (
      this.rowNodeMap.get('units')?.data.value !== '' &&
      Number(this.rowNodeMap.get('units')?.data.value) > 0
    ) {
      this.rowNodeMap
        .get('units')
        ?.setDataValue(
          'value',
          -1 * Math.abs(this.rowNodeMap.get('units')?.data.value),
        )
    }
  }

  adjustPositiveUnits() {
    if (
      this.rowNodeMap.get('dollarAccrued')?.data.value !== '' &&
      Number(this.rowNodeMap.get('dollarAccrued')?.data.value) > 0
    ) {
      this.rowNodeMap
        .get('dollarAccrued')
        ?.setDataValue(
          'value',
          -1 * Math.abs(this.rowNodeMap.get('dollarAccrued')?.data.value),
        )
      this.rowNodeMap
        .get('dollarAccrued')
        ?.setDataValue(
          'thirdParty',
          -1 * Math.abs(this.rowNodeMap.get('dollarAccrued')?.data.value),
        )
    }

    if (
      this.rowNodeMap.get('units')?.data.value !== '' &&
      Number(this.rowNodeMap.get('units')?.data.value) < 0
    ) {
      this.rowNodeMap
        .get('units')
        ?.setDataValue(
          'value',
          Math.abs(this.rowNodeMap.get('units')?.data.value),
        )
    }
  }

  adjustExitFeeReceivable() {
    const tranType = this.rowNodeMap.get('transTypeCode')?.data.value ?? ''
    const exitFeeReceivable =
      this.rowNodeMap.get('exitFeeReceivable')?.data.value ?? 0
    // exit fee receivable should always reduce proceeds on a buy
    if ('BUY' === tranType && exitFeeReceivable > 0) {
      this.rowNodeMap
        .get('exitFeeReceivable')
        ?.setDataValue('value', -exitFeeReceivable)
    }
    // exit fee receivable should always increase proceeds on a sell
    if ('SELL' === tranType && exitFeeReceivable < 0) {
      this.rowNodeMap
        .get('exitFeeReceivable')
        ?.setDataValue('value', Math.abs(exitFeeReceivable))
    }
  }

  editable(params: EditableCallbackParams): boolean {
    // Fields stay editable before an account is picked; validation is deferred instead, and
    // edited rows are flagged until the account is resolved (see onColumnValueChanged).
    return params.data.isEditable
  }

  /**
   * Toggle advanced rows.
   * This is a callback triggered by the html template.
   */
  toggleRows() {
    if (!this.displayAdvancedRows.value) {
      this.removeRowTransaction()
      return
    } else {
      this.applyRowTransaction()
    }
    this.updatePropertiesTitle()
  }

  applyRowTransaction() {
    switch (this.rowNodeMap.get('transactionType')?.data.value) {
      case 'Draw':
        this.adjustInactiveRows(
          'drawType',
          InactiveRowActions.Add,
          'transTypeCode',
        )
        this.adjustInactiveRows(
          'reserveBalanceField',
          InactiveRowActions.Add,
          'drawType',
        )
        this.setDrawDefaultDataMap()
        break
      case 'Buy':
        this.adjustInactiveRows(
          'buyType',
          InactiveRowActions.Add,
          'transTypeCode',
        )
        break
    }
  }

  removeRowTransaction() {
    this.removeDataMapRows('drawType')
    this.removeDataMapRows('reserveBalanceField')
    this.removeDataMapRows('buyType')
  }

  removeDataMapRows(rowToAdjust: string) {
    if (this.inactiveRows.get(rowToAdjust) === undefined) {
      this.adjustInactiveRows(rowToAdjust, InactiveRowActions.Remove)
    }
  }

  private setDataMapFields(currentCategory: string) {
    if (this.dataMap == null) {
      return
    }
    this.dataMap.unfundedToFunded =
      currentCategory == DataMapTypes['Draw from Unfunded'].name
    this.dataMap.interestReserveToFunded =
      currentCategory == DataMapTypes['Draw from Interest Reserve'].name
    this.dataMap.paymentInKind =
      currentCategory == DataMapTypes['Payment In Kind'].name
    this.dataMap.capitalizationOfInterest =
      currentCategory == DataMapTypes['Capitalization of Interest'].name

    // reserveBalanceField is only valid when no draw-type option is selected,
    // so clear it whenever a draw type is chosen.
    if (currentCategory) {
      this.dataMap.reserveBalanceField = undefined
      this.rowNodeMap.get('reserveBalanceField')?.setDataValue('value', '')
    }
  }

  private setReserveBalanceField(label: string) {
    if (this.dataMap == null) {
      return
    }
    // The grid stores the label; persist the enum key on the data map.
    this.dataMap.reserveBalanceField =
      reserveBalanceFieldKeyFromLabel(label) || undefined

    // Selecting a reserve balance clears the mutually-exclusive draw-type
    // options so only one of the two is ever sent on the data map.
    if (label) {
      this.dataMap.unfundedToFunded = false
      this.dataMap.interestReserveToFunded = false
      this.dataMap.capitalizationOfInterest = false
      this.rowNodeMap.get('drawType')?.setDataValue('value', '')
    }
  }

  advancedRowDisplay() {
    this.removeRowTransaction()

    /* If toggle is already clicked then remove extra data first and apply */
    if (this.rowNodeMap.get('transactionType')?.data.value == 'Draw') {
      this.displayAdvancedRows.setValue(true)
    }
    this.toggleRows()
  }

  private setDrawDefaultDataMap() {
    if (!this.dataMap) return

    if (
      this.dataMap.unfundedToFunded == undefined &&
      this.dataMap.interestReserveToFunded == undefined &&
      this.dataMap.capitalizationOfInterest == undefined &&
      !this.dataMap.reserveBalanceField
    ) {
      this.dataMap.unfundedToFunded = true
    }
  }

  validate(api: GridApi, fetchSecurityNode?: boolean) {
    const startTime = Date.now()
    const categoryNode = this.rowNodeMap.get('transactionType')
    const transTypeCodeNode = this.rowNodeMap.get('transTypeCode')
    if (!categoryNode?.data.value || !transTypeCodeNode?.data.value) {
      return
    }
    // Don't validate while an aggregate account is unresolved; there's no concrete
    // account to scope the request to yet (edited fields are flagged instead).
    if (this.aggregateAccountUnresolved) {
      return
    }
    this.isDataValid = false
    const validateView = this.createValidationView(
      this.accountDetails as AccountDetails,
    )
    this.isValidating = true
    this.createEditTransactionService
      .validateTransactionTradeCash(validateView, TranCategoryUrl.TRADE_ENTRY)
      .subscribe(
        validationInfos => {
          if (validationInfos) {
            if (
              this.isCollateralBrokerAccount &&
              !(validateView.userData.transaction as Trade).dataMap
                ?.collateralBroker
            ) {
              validationInfos.push({
                message: 'Collateral Broker must be specified',
                level: ErrorSeverity.ERROR,
                impactedFields: ['collateralBroker'],
              })
            }
            this.handleValidationResponse(validationInfos)
            this.handleValidationResponseForbidden(
              validationInfos.filter(
                validationInfo =>
                  validationInfo.level == ErrorSeverity.FORBIDDEN,
              ),
            )
            this.isValidating = false
            if (
              fetchSecurityNode &&
              this.rowNodeMap.get('cusip')?.data.value !== '' &&
              !validationInfos.some(
                obj =>
                  obj.impactedFields.includes('securityId') &&
                  obj.level === 'ERROR' &&
                  obj.message === 'Valid Value Required',
              )
            ) {
              this.loadSecurityOnCusipChange(
                this.rowNodeMap.get('cusip')?.data.value,
                this.rowNodeMap.get('settleDate')?.data.value,
              )
            }
          }
          const now = Date.now()
          const type = singleDialogMetricsDetail(this.transInput)
          this.telemetryService.emitCustomEvent(
            SINGLE_DIALOG_VALIDATION_EVENT_NAME,
            {
              dialogName: 'Trade-' + type,
              durationMs: now - startTime,
            },
          )
        },
        err => {
          this.errorHeader = TransactionConstants.VALIDATION_ERROR
          this.errorDetails = err.error
          this.isValidating = false
        },
      )
  }

  handleValidationResponseForbidden(validationInfos: ValidationInfo[]) {
    if (validationInfos.length == 0) return
    const forbiddenErrorsList: string[] = []
    validationInfos.forEach(validationInfo => {
      forbiddenErrorsList.push(validationInfo.message)
    })
    const validatorKey = 'forbiddenErrorsValidator'
    this.forbiddenErrors.markAsTouched()
    this.forbiddenErrors.setErrors({
      [validatorKey]: {
        message: forbiddenErrorsList,
      },
    })
    this.isDataValid = false
  }

  handleValidationResponse(validationInfos: ValidationInfo[]) {
    const errorMessages: Map<string, string[]> = new Map()
    const warningMessages: Map<string, string[]> = new Map()
    validationInfos.forEach(validationInfo => {
      if (validationInfo.level == ErrorSeverity.ERROR) {
        validationInfo.impactedFields.forEach(impactedField => {
          if (errorMessages.has(impactedField)) {
            errorMessages.get(impactedField)?.push(validationInfo.message)
          } else {
            errorMessages.set(impactedField, [validationInfo.message])
          }
        })
      } else {
        validationInfo.impactedFields.forEach(impactedField => {
          if (warningMessages.has(impactedField)) {
            warningMessages.get(impactedField)?.push(validationInfo.message)
          } else {
            warningMessages.set(impactedField, [validationInfo.message])
          }
        })
      }
    })
    this.isDataValid = errorMessages.size == 0
    this.rowNodeMap.forEach((value: RowNode) => {
      handleValidation(value, 'VALID', '')
    })
    this.note.setErrors(null)
    errorMessages.forEach((value: string[], key: string) => {
      let currentNode = this.getCurrentNode(key)
      if (key == 'accountId') currentNode = this.rowNodeMap.get('account')
      if (currentNode) {
        handleValidation(currentNode, 'INVALID', value.join(' | '))
        if (key == 'tranType') {
          const transTypCode = this.rowNodeMap.get('transTypeCode')
          if (transTypCode)
            handleValidation(transTypCode, 'INVALID', value.join(' | '))
        }
        if (key == 'accrued') {
          const dollarAccrued = this.rowNodeMap.get('dollarAccrued')
          if (dollarAccrued)
            handleValidation(dollarAccrued, 'INVALID', value.join(' | '))
        }
      } else if (key == 'transactionNote') {
        const validatorKey = 'transactionNoteValidator'
        this.note.markAsTouched()
        this.note.setErrors({
          [validatorKey]: {
            message: value,
          },
        })
      }
    })
    warningMessages.forEach((value: string[], key: string) => {
      let currentNode = this.getCurrentNode(key)
      if (key == 'accountId') currentNode = this.rowNodeMap.get('account')
      if (currentNode && currentNode.data.errorMessage == '') {
        handleValidation(currentNode, 'WARNING', value.join(' | '))
        if (key == 'tranType') {
          const transTypCode = this.rowNodeMap.get('transTypeCode')
          if (transTypCode)
            handleValidation(transTypCode, 'WARNING', value.join(' | '))
        }
        if (key == 'accrued') {
          const decimalAccrued = this.rowNodeMap.get('decimalAccrued')
          if (decimalAccrued)
            handleValidation(decimalAccrued, 'WARNING', value.join(' | '))
        }
      } else if (
        key == 'transactionNote' &&
        !errorMessages.has('transactionNote')
      ) {
        const validatorKey = 'transactionNoteValidator'
        this.noteWarning = {
          [validatorKey]: {
            message: value,
          },
        }
      }
    })
  }

  getCurrentNode(key: string): RowNode | undefined {
    switch (key) {
      case 'securityId':
        return this.rowNodeMap.get('cusip')
      case 'tranType':
        return this.rowNodeMap.get('transactionType')
      case 'amount':
        return this.rowNodeMap.get('proceeds')
      case 'accrued':
        return this.rowNodeMap.get('decimalAccrued')
      default:
        return this.rowNodeMap.get(key)
    }
  }

  createValidationView(
    accountDetails: AccountDetails,
  ): TradeEntryValidationView {
    const view: TradeEntryValidationView = {
      userData: {
        transaction: this.prepareTransactionData('value', true),
        cusip: this.rowNodeMap.get('cusip')?.data.value,
        existingUnits:
          this.originalTransaction?.units != null
            ? this.originalTransaction.units
            : 0,
        dollarAccrued: Number(this.rowNodeMap.get('dollarAccrued')?.data.value),
      },
      accountDetails: accountDetails,
      requestMode: getRequestModeFromModalMode(this.transInput),
      windowDate: this.windowDate ?? new Date(),
      reconDate: this.reconDate ?? new Date(),
      sessionId: this.sessionId ?? '',
    }

    if (this.note.value != '') {
      view.transactionNote = this.note.value
    }
    if (this.hasTransactionMatchCustody) {
      view.custodyData =
        this.transactionMatchesCustody &&
        this.transactionMatchesCustody.length > 1
          ? prepareMultiViewForMultiMatchValidations(
              this.transactionMatchesCustody,
            )
          : [
              {
                transaction: this.prepareTransactionData('custody', true),
                cusip: this.rowNodeMap.get('cusip')?.data.custody,
              },
            ]
    }
    if (this.hasTransactionMatchTrading) {
      view.tradingData =
        this.transactionMatchesTrading &&
        this.transactionMatchesTrading.length > 1
          ? prepareMultiViewForMultiMatchValidations(
              this.transactionMatchesTrading,
            )
          : [
              {
                transaction: this.prepareTransactionData('trading', true),
                cusip: this.rowNodeMap.get('cusip')?.data.trading,
              },
            ]
    }
    view.calculatedData = {
      transaction: this.prepareTransactionData('thirdParty', true),
      cusip: this.rowNodeMap.get('cusip')?.data.thirdParty,
    }

    if (this.externalAccountId) {
      view.externalTransaction = {
        accountId: this.externalAccountId,
        isSleeveSimple: this.isExternalAccountSleeveSimple,
      } as ValidationExternalTransactionData
    }
    return view
  }

  save() {
    if (!this.isDataValid || !this.sessionId) {
      return
    }
    this.store.dispatch(
      setCommandsMetrics({
        command: 'tradeEntry',
        time: Date.now(),
      }),
    )
    this.formSubmitting = true
    this.loadingText = TransactionConstants.SAVE_TRANSACTION
    let unclearTransaction = false
    if (this.originalTransaction?.clearedDate && !this.clearTransaction.value) {
      unclearTransaction = true
    }
    const tranCommand: CreateEditTransactionRequest = {
      transaction: this.prepareTransactionData('value', false),
      tranCategory: TranCategory.TRADE,
      metadata: createMetadata(
        this.reconDate ?? new Date(),
        this.rowNodeMap.get('cusip')?.data.value,
        unclearTransaction,
        this.reprocessTransfers.value,
      ),
    }
    if (this.note.value != '') {
      tranCommand.transactionNote = { newValue: this.note.value }
    }
    if (this.originalTransaction?.transactionId) {
      tranCommand.notes = TransactionConstants.HELIOS_TRADE_SAVE_NOTE_CW_EDIT
      this.createEditTransactionService
        .editTransaction(
          this.sessionId,
          this.originalTransaction.transactionId,
          tranCommand,
        )
        .subscribe(
          () => {
            this.toastService.success(
              'Clearwater Transaction edit command successfully submitted.',
            )
            this.formSubmitting = false
            this.closeModal()
          },
          error => {
            this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
            this.errorDetails = error.error
            this.formSubmitting = false
          },
        )
    } else {
      if (isExternalTransaction(this.transInput)) {
        tranCommand.notes = TransactionConstants.HELIOS_TRADE_SAVE_NOTE_EXTERNAL
        tranCommand.extTran = {
          externalAccountId: this.externalAccountId ?? 0,
          executionId: this.executionId ?? 0,
          externalTranId: Number(this.transInput?.transactionId),
          originalCusip: this.originalCusip,
          status: this.transInput?.externalTransactionStatus,
        }

        if (this.externalTranTypeLabel?.id) {
          tranCommand.transaction.typeLabel = {
            id: this.externalTranTypeLabel.id,
            label: this.externalTranTypeLabel.label,
            description: this.externalTranTypeLabel.description,
          }
        }

        this.createEditTransactionService
          .saveExternalMatch(this.sessionId, tranCommand)
          .subscribe(
            () => {
              this.toastService.success(
                'External Transaction command successfully submitted.',
              )
              this.formSubmitting = false
              this.closeModal()
            },
            error => {
              this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
              this.errorDetails = error.error
              this.formSubmitting = false
            },
          )
      } else {
        tranCommand.notes =
          TransactionConstants.HELIOS_TRADE_SAVE_NOTE_CW_CREATE
        this.createEditTransactionService
          .createTransaction(this.sessionId, tranCommand)
          .subscribe(
            () => {
              this.toastService.success(
                'Clearwater Transaction create command successfully submitted.',
              )
              this.formSubmitting = false
              this.closeModal()
            },
            error => {
              this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
              this.errorDetails = error.error
              this.formSubmitting = false
            },
          )
      }
    }
  }

  private prepareTransactionData(
    column: 'value' | 'custody' | 'trading' | 'thirdParty',
    isValidate: boolean,
  ): Trade {
    let createdDate = new Date().toISOString()
    if (isEditModal(this.transInput) && this.originalTransaction?.createdDate) {
      createdDate = new Date(
        this.originalTransaction?.createdDate,
      ).toISOString()
    }
    const cp =
      this.counterPartyList.length > 0
        ? getCounterPartyFromName(
            this.rowNodeMap.get('clearwaterCounterParty')?.data.value,
            this.counterPartyList,
          )
        : getDefaultUnknownCounterParty()

    const amortTargetDate = this.rowNodeMap.get('amortTargetDate')?.data[column]
    const amortTargetPrice =
      this.rowNodeMap.get('amortTargetPrice')?.data[column]
    const priceNode = this.rowNodeMap.get('price')
    const unitsNode = this.rowNodeMap.get('units')
    const decimalAccruedNode = this.rowNodeMap.get('decimalAccrued')
    const factorNode = this.rowNodeMap.get('factor')
    if (this.isCollateralBrokerAccount) {
      this.collateralBroker =
        this.rowNodeMap.get('collateralBroker')?.data.value
      if (this.originalTransaction?.dataMap && this.collateralBroker) {
        const dataMapWithCollateralBroker = this.originalTransaction.dataMap
        dataMapWithCollateralBroker.collateralBroker = this.collateralBroker
        this.dataMap = dataMapWithCollateralBroker
      } else if (this.dataMap) {
        this.dataMap.collateralBroker = this.collateralBroker
      } else {
        this.dataMap = { collateralBroker: this.collateralBroker }
      }
    }
    const transactionData: Trade = {
      commission: Number(this.rowNodeMap.get('commission')?.data[column]),
      yield: this.yield,
      counterParty: cp && cp.id != -1 ? cp : getDefaultUnknownCounterParty(),
      counterPartyId:
        cp && cp.id != -1 ? cp.id : getDefaultUnknownCounterParty().id,
      postDate: this.rowNodeMap.get('postDate')?.data[column],
      amount: Number(this.rowNodeMap.get('proceeds')?.data[column]),
      tranType: this.rowNodeMap.get('transTypeCode')?.data[column],
      createdDate: createdDate,
      modifiedDate: new Date().toISOString(),
      active: true,
      amortOverrideType: 'OBEY_ACCOUNT_SECURITY', //get from transaction if available or from securityMasterData
      discount: calculateDiscount(
        this.rowNodeMap.get('price')?.data[column],
        new Date(this.rowNodeMap.get('settleDate')?.data[column]),
        this.security,
      ),
      userId: this.userId ?? 0,
      revisionId: isEditModal(this.transInput)
        ? (this.originalTransaction?.revisionId ?? 0) + 1
        : 1,
      sourceId: TransactionConstants.HELIOS_SOURCE_ID,
      accountId: this.accountId ?? 0,
      entryDate: this.rowNodeMap.get('entryDate')?.data[column],
      settleDate: this.rowNodeMap.get('settleDate')?.data[column],
      valuationDate: this.rowNodeMap.get('settleDate')?.data[column],
      dataMap: this.dataMap as TradeDataMap,
      traderId: 0,
    }

    // Typescript thinks transactionData.dataMap is undefined unless we wrapped this up in this if statement
    if (!transactionData.dataMap) {
      transactionData.dataMap = this.dataMap as TradeDataMap
    }

    if (priceNode && priceNode.data[column] !== '') {
      transactionData.price = Number(this.rowNodeMap.get('price')?.data[column])
    }
    if (unitsNode && unitsNode.data[column] !== '') {
      transactionData.units = Number(this.rowNodeMap.get('units')?.data[column])
    }
    if (decimalAccruedNode && decimalAccruedNode.data[column] !== '') {
      transactionData.accrued = Number(
        this.rowNodeMap.get('decimalAccrued')?.data[column],
      )
    }
    if (factorNode && factorNode.data[column] !== '') {
      transactionData.factor = Number(
        this.rowNodeMap.get('factor')?.data[column],
      )
    }

    if (!isValidate) {
      transactionData.securityId = this.security
        ? this.security.securityMasterData?.id
        : 0
    }
    if (transactionData.tranType == 'DRAW') {
      if (this.dataMap?.interestReserveToFunded == true) {
        transactionData.dataMap.interestReserveToFunded =
          this.dataMap.interestReserveToFunded
        transactionData.typeLabelId = -1477
      } else if (this.dataMap?.unfundedToFunded == true) {
        transactionData.dataMap.unfundedToFunded = this.dataMap.unfundedToFunded
        transactionData.typeLabelId = -1478
      } else if (this.dataMap?.capitalizationOfInterest == true) {
        transactionData.dataMap.capitalizationOfInterest =
          this.dataMap.capitalizationOfInterest
        transactionData.typeLabelId = -3677
      } else if (this.dataMap?.reserveBalanceField) {
        transactionData.dataMap.reserveBalanceField =
          this.dataMap.reserveBalanceField
      }
    }
    if (transactionData.tranType == 'BUY') {
      if (this.dataMap?.paymentInKind == true) {
        transactionData.dataMap.paymentInKind = this.dataMap.paymentInKind
        transactionData.typeLabelId = -1354
      }
    }
    if (this.rowNodeMap.get('originationFee')?.data?.value) {
      if (Calculators.isBankOrMortgageLoan(this.security)) {
        transactionData.dataMap.originationFee = Number(
          this.rowNodeMap?.get('originationFee')?.data?.value,
        )
      }
    }
    if (this.rowNodeMap.get('exitFee')?.data?.value) {
      if (Calculators.isBankOrMortgageLoan(this.security)) {
        transactionData.dataMap.exitFee = Number(
          this.rowNodeMap?.get('exitFee')?.data?.value,
        )
      }
    }
    if (this.rowNodeMap.get('exitFeeReceivable')?.data?.value) {
      if (Calculators.isBankOrMortgageLoan(this.security)) {
        transactionData.dataMap.exitFeeReceivable = Number(
          this.rowNodeMap?.get('exitFeeReceivable')?.data?.value,
        )
      }
    }
    if (this.rowNodeMap.get('drawFee')?.data?.value) {
      if (Calculators.isBankOrMortgageLoan(this.security)) {
        transactionData.dataMap.drawFee = Number(
          this.rowNodeMap?.get('drawFee')?.data?.value,
        )
      }
    }
    if (this.rowNodeMap.get('prepaymentPenaltyFee')?.data?.value) {
      if (Calculators.isBankOrMortgageLoan(this.security)) {
        transactionData.dataMap.prepaymentPenaltyFee = Number(
          this.rowNodeMap?.get('prepaymentPenaltyFee')?.data?.value,
        )
      }
    }
    if (this.rowNodeMap.get('prepaidInterest')?.data?.value) {
      if (Calculators.isBankOrMortgageLoan(this.security)) {
        transactionData.dataMap.prepaidInterest = Number(
          this.rowNodeMap?.get('prepaidInterest')?.data?.value,
        )
      }
    }
    if (this.rowNodeMap.get('interestDue')?.data?.value) {
      if (Calculators.isBankOrMortgageLoan(this.security)) {
        transactionData.dataMap.interestDue = Number(
          this.rowNodeMap?.get('interestDue')?.data?.value,
        )
      }
    }
    if (Calculators.isBankOrMortgageLoan(this.security)) {
      Object.assign(
        transactionData.dataMap,
        getLoanFieldValues(field => this.rowNodeMap.get(field)?.data?.value),
      )
    }
    if (amortTargetDate && amortTargetDate !== '') {
      transactionData.amortTargetDate = amortTargetDate
    }
    if (amortTargetPrice && amortTargetPrice !== '') {
      transactionData.amortTargetPrice = Number(amortTargetPrice)
    }
    const inventoryNode = this.rowNodeMap.get('inventoryType')
    if (inventoryNode?.data.value && inventoryNode.data.value !== '') {
      transactionData.inventoryType = inventoryNode.data[
        column
      ] as InventoryType
    }
    if (this.clearTransaction.value) {
      transactionData.clearedDate = isValidate
        ? new Date().toISOString().split('Z')[0]
        : new Date().toISOString()
    }

    if (this.originalTransaction) {
      transactionData.transactionId = this.originalTransaction.transactionId
      transactionData.legacyOrigTranId =
        this.originalTransaction.legacyOrigTranId
      transactionData.legacyTranId = this.originalTransaction.legacyTranId
    }
    if (!isValidate && this.originalTransaction?.typeLabelId) {
      transactionData.typeLabelId = this.originalTransaction.typeLabelId
    }
    return transactionData
  }

  clearUnclearTransaction(date: string, nodeId: 'postDate' | 'settleDate') {
    if (
      isAfter(
        new Date(date),
        this.reconDate
          ? new Date(this.reconDate as unknown as string)
          : new Date(),
      )
    ) {
      this.clearTransaction.patchValue(false)
    } else {
      if (
        (nodeId == 'postDate' && !this.isSettleDateFuture) ||
        (nodeId == 'settleDate' && !this.isPostDateFuture)
      ) {
        this.clearTransaction.patchValue(true)
      }
    }
  }

  unclearTransactionInitially() {
    const postDate = this.rowData.find(row => row.id == 'postDate')?.value ?? ''
    const settleDate =
      this.rowData.find(row => row.id == 'settleDate')?.value ?? ''
    this.isPostDateFuture = isAfter(
      new Date(postDate),
      this.reconDate
        ? new Date(this.reconDate as unknown as string)
        : new Date(),
    )
    this.isSettleDateFuture = isAfter(
      new Date(settleDate),
      this.reconDate
        ? new Date(this.reconDate as unknown as string)
        : new Date(),
    )
    if (this.isPostDateFuture || this.isSettleDateFuture) {
      this.clearTransaction.patchValue(false)
    }
  }

  private closeModal() {
    this.activeModal.close()
  }

  /**
   * price modal opener opener
   *
   */
  loadPriceModal() {
    const modalRef = this.modalService.open(CalculatePriceComponent, {
      size: 'md',
      windowClass: 'modal-window-click-through',
      keyboard: false,
    } as NgbModalOptions)
    modalRef.componentInstance.rowNodeMap = this.rowNodeMap
    modalRef.componentInstance.security = this.security
  }

  isPriceRequestEnabled() {
    return (
      this.transactionLoaded &&
      (this.hasTransactionMatchCustody || this.hasTransactionMatchTrading)
    )
  }

  fetchSleeveSimples(accountId: number) {
    const sleeveSimples$ = this.createEditTransactionService
      .fetchSubAccounts(accountId)
      .pipe(retry(2))
    sleeveSimples$.subscribe(
      account => {
        this.sleeveSimpleAccount = account
        // Open the picker only once its options exist (opening earlier leaves the
        // select stuck loading). Skip when an external post already filled the account.
        if (
          this.aggregateAccountUnresolved &&
          this.gridApi?.api &&
          !this.isExternalAccountSleeveSimple
        ) {
          focusFieldRowEditor(this.gridApi.api, 'account')
        }
      },
      err => {
        this.errorHeader = TransactionConstants.ERROR_LOAD_SLEEVE_ACCOUNT
        this.errorDetails = err.error
      },
    )
  }

  async loadAggAccountDetails(accountDisplayName: string) {
    const account = this.sleeveSimpleAccount.find(
      name => name.displayLabel == accountDisplayName,
    )
    if (account) {
      await this.loadAndMapAccountDetailsForSleeve(account.id)
      // A concrete account is now resolved; clear the account-unresolved marks, replay any
      // lookups deferred while it was unresolved (e.g. Asset ID entered first), and validate.
      clearAllInvalidations(this.gridApi?.api)
      // External posts already loaded their security via the external-transaction path and
      // derived the trade fields from the external txn; replaying the cusip lookup would
      // re-derive them from create-mode logic and clobber those values.
      if (!isExternalTransaction(this.transInput)) {
        replayDeferredLookups(this.gridApi?.api, [
          {
            rowId: 'cusip',
            run: cusip =>
              this.loadSecurityOnCusipChange(
                cusip,
                this.rowNodeMap.get('settleDate')?.data.value,
              ),
          },
        ])
      }
      if (this.gridApi) {
        this.validate(this.gridApi.api)
      }
    }
  }

  setAccountDisplayName() {
    const accountRowField = this.rowData?.find(row => row.id == 'account')
    // Clear an aggregate account and force a sub-account pick, unless a sleeve-simple post
    // already resolved it to its concrete sub-account.
    if (
      this.accountDetails?.isAggregate &&
      !this.isExternalAccountSleeveSimple
    ) {
      if (accountRowField) {
        accountRowField.isEditable = true
        accountRowField.value = ''
        accountRowField.trading = ''
        accountRowField.custody = ''
        // Flag the account row so it's obvious it must be filled before validation.
        accountRowField.validityStatus = 'invalid'
        accountRowField.errorMessage = ACCOUNT_REQUIRED_ERROR
      }
      this.aggregateAccountUnresolved = true
      this.gridApi?.api.refreshCells({ force: true })
      this.fetchSleeveSimples(this.accountDetails?.accountId)
      this.resetAggAccountDetails()
    } else {
      this.accountName =
        this.accountDetails?.accountId +
        ' - ' +
        this.accountDetails?.accountName
      if (accountRowField) {
        accountRowField.value = this.accountName
      }
    }
  }

  resetAggAccountDetails() {
    this.accountId = undefined
    this.accountDetails = undefined
  }

  disableGridFocus() {
    this.gridApi?.api.clearRangeSelection()
  }

  async loadAndMapAccountDetailsForSleeve(accountId: number) {
    const inventoryConfig$: Observable<LotInventoryConfig> =
      this.createEditTransactionService.loadInventoryConfig(accountId)

    const accountDetails$: Observable<AccountDetails> =
      this.createEditTransactionService.loadAccountDetails(accountId)

    return Promise.all([
      accountDetails$.toPromise(),
      inventoryConfig$.toPromise(),
    ])
      .then(([accountDetails, inventoryConfig]) => {
        this.accountDetails = accountDetails
        this.accountId = accountDetails.accountId
        this.isCollateralBrokerAccount =
          inventoryConfig.inventoryGroupMethod == 'COLLATERAL_BROKER'
        // A concrete account is now resolved; unlock the rest of the fields.
        this.aggregateAccountUnresolved = false
        this.gridApi?.api.refreshCells({ force: true })
        this.determineAndPlugCollateral()
      })
      .catch(error => {
        this.errorDetails = error.error
      })
  }

  determineAndPlugCollateral() {
    if (
      this.isCollateralBrokerAccount &&
      !this.rowData.find(row => row.id == 'collateralBroker')
    ) {
      const collateralRow = getCollateralBroker(this.collateralBroker)
      this.rowData?.push(collateralRow)
      this.gridApi?.api.applyTransaction({
        add: [collateralRow],
      })
    }
  }

  matchCellDoubleClick(copiedData: CellMatched) {
    this.rowNodeMap
      .get(copiedData.field)
      ?.setDataValue('value', copiedData.value)
  }

  /**
   * get cell class on validityStatus
   *
   * @returns cell class
   */
  deduceValidityStatusCellClass(params: CellClassParams, multiFlag: boolean) {
    return multiFlag && !this.consolidatedFields.has(params.data.id)
      ? { 'background-color': 'lightgrey' }
      : undefined
  }

  @HostListener('click') dialogClicked() {
    if (isDialogComponentInActive(this.compId)) {
      activateDialog(this.activeInstances, this.compId, this.elRef)
    }
  }

  getComponentId() {
    return this.compId
  }

  getCounterPartyOptions() {
    if (this.counterPartyList.length < 4) {
      return {
        itemsAsync: this.createEditTransactionService.loadCounterParties(),
        bindValue: 'name',
        bindLabel: 'name',
      }
    } else {
      return {
        items: this.counterPartyList,
        bindValue: 'name',
        bindLabel: 'name',
      }
    }
  }

  checkRowEligibilities() {
    this.checkColumnEligibility(
      'exitFee',
      ['Buy', 'Maturity', 'Principal Repayment'],
      'commission',
    )
    this.checkColumnEligibility(
      'exitFeeReceivable',
      ['Buy', 'Sell'],
      'commission',
    )
    this.checkColumnEligibility('drawFee', ['Draw'], 'commission')
    this.checkColumnEligibility(
      'prepaymentPenaltyFee',
      ['Principal Repayment', 'Maturity'],
      'exitFee',
    )
    this.checkColumnEligibility('originationFee', ['ALL'], 'commission')
    this.checkColumnEligibility(
      'prepaidInterest',
      ['Buy', 'Draw'],
      'originationFee',
    )
    this.checkColumnEligibility('interestDue', ['Buy'], 'prepaidInterest')
    this.checkColumnEligibility(
      'interestBearingInterestReserve',
      ['Buy', 'Sell'],
      'exitFeeReceivable',
    )
    this.checkColumnEligibility(
      'interestBearingPrincipalReserve',
      ['Buy', 'Sell'],
      'interestBearingInterestReserve',
    )
    this.checkColumnEligibility(
      'nonInterestBearingInterestReserve',
      ['Buy', 'Sell'],
      'interestBearingPrincipalReserve',
    )
    this.checkColumnEligibility(
      'nonInterestBearingPrincipalReserve',
      ['Buy', 'Sell'],
      'nonInterestBearingInterestReserve',
    )

    this.advancedRowDisplay()
  }

  // This method is used for checking Origination, Exit , Draw , Prepayment Penalty Fee, Prepaid Interest and Interest Due eligibility.
  checkColumnEligibility(
    columnName: string,
    applicableTransactionTypes: string[],
    precedingColumnName: string,
  ) {
    const isTransactionType = (element: { id: string }) =>
      element.id == 'transactionType'
    const transactionTypeIndex = this.rowData.findIndex(isTransactionType)

    const isEligable = Calculators.isBankOrMortgageLoan(this.security)
    const isApplicableTransaction =
      applicableTransactionTypes[0] == 'ALL' ||
      applicableTransactionTypes.find(
        element => element == this.rowData[transactionTypeIndex]?.value,
      )
    const isColumn = (element: { id: string }) => element.id == columnName
    const columnIndex = this.rowData.findIndex(isColumn)
    if (
      (!isEligable || !this.security || !isApplicableTransaction) &&
      columnIndex >= 0
    ) {
      this.adjustInactiveRows(columnName, InactiveRowActions.Remove)
    } else if (isEligable && columnIndex == -1 && isApplicableTransaction) {
      this.adjustInactiveRows(
        columnName,
        InactiveRowActions.Add,
        precedingColumnName,
      )
    }
  }

  adjustInactiveRows(
    rowToAdjust: string,
    action: InactiveRowActions,
    rowToAddAfter?: string,
  ) {
    const relativePositionComparator = (element: { id: string }) =>
      element.id == rowToAddAfter
    let positionToBeInserted = this.rowData.findIndex(
      relativePositionComparator,
    )
    const rowToAdjustComparator = (element: { id: string }) =>
      element.id == rowToAdjust
    const rowToAdjustIndex = this.rowData.findIndex(rowToAdjustComparator)
    const updatedRowData = [...this.rowData]
    switch (action) {
      case InactiveRowActions.Add:
        if (this.inactiveRows.get(rowToAdjust) == undefined) {
          break
        }
        if (positionToBeInserted >= 0) {
          positionToBeInserted += 1 //Bump this forward for fields that exist, so we splice after the relative position.
        }
        updatedRowData.splice(
          positionToBeInserted,
          0,
          this.inactiveRows.get(rowToAdjust),
        )
        this.setRowData(updatedRowData)
        this.inactiveRows.delete(rowToAdjust)
        break
      case InactiveRowActions.Remove:
        this.inactiveRows.set(rowToAdjust, this.rowData[rowToAdjustIndex])
        updatedRowData.splice(rowToAdjustIndex, 1)
        this.setRowData(updatedRowData)
        this.rowNodeMap.delete(rowToAdjust)
        break
    }
  }

  updatePropertiesTitle() {
    let title = ''
    title += this.displayDynamicRows.value ? 'Showing Dynamic Rows. ' : ''
    if (this.displayAdvancedRows.value) title += 'Showing Advanced Rows. '

    if (title === '') title = 'No Table Properties set'

    this.propertiesTitle = title
  }

  toggleDynamicRows() {
    if (this.displayDynamicRows.value) {
      this.applyDynamicRowTransaction()
    } else {
      this.removeDynamicRowTransaction()
    }
    this.updatePropertiesTitle()
  }

  applyDynamicRowTransaction() {
    this.gridApi?.api.applyTransaction({ add: this.dynamicRows })
  }

  removeDynamicRowTransaction() {
    this.gridApi?.api.applyTransaction({ remove: this.dynamicRows })
  }

  generateDynamicRows(
    matchTrading?: TransactionMatch,
    matchCustody?: TransactionMatch,
  ) {
    if (!matchTrading && !matchCustody) {
      return
    }
    const custodyAggInfo = matchCustody?.aggInfoString
      ? JSON.parse('{ ' + matchCustody?.aggInfoString + ' }')
      : undefined
    const tradingAggInfo = matchTrading?.aggInfoString
      ? JSON.parse('{ ' + matchTrading?.aggInfoString + ' }')
      : undefined

    this.dynamicRows = createDynamicRowsForCWTransaction(
      custodyAggInfo || {},
      tradingAggInfo || {},
    ) as TradeEntryModel[]
  }
}

export enum InactiveRowActions {
  Add,
  Remove,
}
